import { isEmpty } from 'lodash-es';

import {
  IPublisher,
  IReport,
  IUser,
  REPORTTYPE_ADVERTISER_RULES,
  REPORTTYPE_BID_FLOOR_SETTINGS_REPORT,
  REPORTTYPE_BID_SAVER_TRUE_LIFT,
  REPORTTYPE_BUYERS_APS,
  REPORTTYPE_CREATIVE_REVIEW,
  REPORTTYPE_CREATIVE_VALIDATION,
  REPORTTYPE_ELEA_AI_ENRICHMENT_BILLING,
  REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS,
  REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS_SUPER,
  REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER,
  REPORTTYPE_HEADERBIDDER_BYAUCTIONS,
  REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE,
  REPORTTYPE_HEADERBIDDER_BYBUYERS,
  REPORTTYPE_HEADERBIDDER_CAMPAIGN,
  REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS,
  REPORTTYPE_HEADERBIDDER_DEALS,
  REPORTTYPE_HEADERBIDDER_DESTINATIONS,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION,
  REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL,
  REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES,
  REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS,
  REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT,
  REPORTTYPE_IAS_CONTENT_LEVEL_PERFORMANCE,
  REPORTTYPE_IAS_DEVICE,
  REPORTTYPE_IAS_FIRST_PARTY,
  REPORTTYPE_IAS_PERFORMANCE,
  REPORTTYPE_IAS_THIRD_PARTY,
  REPORTTYPE_IAS_YOUTUBE,
  REPORTTYPE_POD_HOURLY,
  REPORTTYPE_SHARE_OF_VOICE,
  REPORTTYPE_STREAM,
  REPORTTYPE_STREAM_USER_ACTIVITY,
  REPORTTYPE_THIRD_PARTY_TRACKER_DOMAINS,
  REPORTTYPE_TTD,
} from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import { ReportTypeID } from '~/src/dash/apiV2/reporting/reportTypes';
import { ISelectModel } from '~/src/dash/components/model';
import {
  getResellerTitle,
  isIntegralplatform,
  isOttera,
  isRoku,
} from '~/src/dash/resellers/utils/importResellerConfig';
import { IMenuList } from '~/src/dash/types/menuList';
import { getReportGraphID, keyToSQLField } from '~/src/dash/utils/graph';
import { PID } from '~/src/dash/utils/publishers';
import { hasPermSuperUser, hasRoleAdmin, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isOpenPathEnabled, isPhiloPublisher, isSamsungPublisher } from '~/src/dash/utils/ui';

export const FrequencyDaily: string = '1';
export const FrequencyWeekly: string = '2';
export const FrequencyMonthly: string = '3';

// Get the report types
export const getReportHeaderBidderTypes = (
  publisher: IPublisher,
  user: IUser,
  fwPublisherIDs: number[],
  hasPublisherEnabledBrandRecognition: boolean,
  hasPublisherEnabledLanguageDetection: boolean,
): IMenuList => {
  const reportTypes = {} as IMenuList;
  const isSuperUser = hasRoleSuperUser(user);

  // base report types - available for everyone <3
  reportTypes[REPORTTYPE_HEADERBIDDER_BYAUCTIONS] = {
    title: 'Auction Report',
    ID: REPORTTYPE_HEADERBIDDER_BYAUCTIONS,
    selected: false,
  };
  reportTypes[REPORTTYPE_HEADERBIDDER_BYBUYERS] = {
    title: 'Bidder Report',
    ID: REPORTTYPE_HEADERBIDDER_BYBUYERS,
    selected: false,
  };
  reportTypes[REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT] = {
    title: 'Unique User Report',
    ID: REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT,
    selected: false,
  };
  reportTypes[REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE] = {
    title: 'Auction Fill Rate Report',
    ID: REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE,
    selected: false,
  };
  reportTypes[REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER] = {
    title: 'Brand Safety Tracker Report',
    ID: REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER,
    selected: false,
  };
  reportTypes[REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS] = {
    title: 'Outbound Deals Report', // In the code, this is referred to as 'Publica Deal Report'
    ID: REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS,
    selected: false,
  };
  reportTypes[REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES] = {
    title: 'Inventory Routes Report',
    ID: REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES,
    selected: false,
  };
  reportTypes[REPORTTYPE_HEADERBIDDER_DESTINATIONS] = {
    title: 'Destinations Report',
    ID: REPORTTYPE_HEADERBIDDER_DESTINATIONS,
    selected: false,
  };
  reportTypes[REPORTTYPE_THIRD_PARTY_TRACKER_DOMAINS] = {
    title: 'Third Party Tracker Domain Report',
    ID: REPORTTYPE_THIRD_PARTY_TRACKER_DOMAINS,
    selected: false,
  };

  if (![PID.samsungNA, PID.philo, PID.weatherNation].includes(publisher.ID)) {
    reportTypes[REPORTTYPE_BID_SAVER_TRUE_LIFT] = {
      title: 'Publica Vault True Lift Report',
      ID: REPORTTYPE_BID_SAVER_TRUE_LIFT,
      selected: false,
    };
  }

  if (
    (publisher.ID === PID.samsungNA &&
      (hasPermSuperUser(publisher.ID, user.PermissionRole.id) || hasRoleAdmin(user))) ||
    isSuperUser
  ) {
    // Samsung (NA) Admin and Superadmin only
    reportTypes[REPORTTYPE_BID_FLOOR_SETTINGS_REPORT] = {
      title: 'Bid Floor Settings Report',
      ID: REPORTTYPE_BID_FLOOR_SETTINGS_REPORT,
      selected: false,
    };
  }

  if (isPhiloPublisher(publisher.ID)) {
    reportTypes[REPORTTYPE_SHARE_OF_VOICE] = {
      title: 'Share of Voice Report',
      ID: REPORTTYPE_SHARE_OF_VOICE,
      selected: false,
    };
  }

  if (fwPublisherIDs.includes(parseInt(publisher.ID, 10)) || isSuperUser) {
    reportTypes[REPORTTYPE_HEADERBIDDER_DEALS] = {
      title: 'Deals Report *',
      ID: REPORTTYPE_HEADERBIDDER_DEALS,
      selected: false,
    };
  }

  // available only for publishers with EleaAI enabled
  if (hasPublisherEnabledBrandRecognition) {
    reportTypes[REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL] = {
      title: 'Elea AI Scan All Report',
      ID: REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL,
      selected: false,
    };

    reportTypes[REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT] = {
      title: 'Elea AI Enrichment Report',
      ID: REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT,
      selected: false,
    };

    reportTypes[REPORTTYPE_ELEA_AI_ENRICHMENT_BILLING] = {
      title: 'Elea AI Enrichment Billing Report',
      ID: REPORTTYPE_ELEA_AI_ENRICHMENT_BILLING,
      selected: false,
    };
  }

  if (hasPublisherEnabledLanguageDetection) {
    reportTypes[REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION] = {
      title: 'Elea AI Language Detection Report',
      ID: REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION,
      selected: false,
    };
  }

  // available for everyone EXCEPT roku
  if (!isRoku()) {
    reportTypes[REPORTTYPE_HEADERBIDDER_CAMPAIGN] = {
      title: 'Campaign Report',
      ID: REPORTTYPE_HEADERBIDDER_CAMPAIGN,
      selected: false,
    };
    // Audience Segment Report only supports liveramp partners
    // in the future, add more conditionals if reporting can handle other partners
    if (publisher.LiverampEnabled === EBool.EBool_TRUE) {
      reportTypes[REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS] = {
        title: 'Audience Segment Liveramp Report',
        ID: REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS,
        selected: false,
      };
    }
  }

  if (publisher.ID === PID.publica) {
    reportTypes[REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS_SUPER] = {
      title: 'Audience Segment Report For All Publishers',
      ID: REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS_SUPER,
      selected: false,
    };
  }

  if (isPhiloPublisher(publisher.ID)) {
    reportTypes[REPORTTYPE_ADVERTISER_RULES] = {
      title: 'Advertiser Rules Report',
      ID: REPORTTYPE_ADVERTISER_RULES,
      selected: false,
    };

    reportTypes[REPORTTYPE_POD_HOURLY] = {
      title: 'Pod Hourly Report',
      ID: REPORTTYPE_POD_HOURLY,
      selected: false,
    };
  }

  if (publisher.ID === PID.aps || isPhiloPublisher(publisher.ID)) {
    reportTypes[REPORTTYPE_BUYERS_APS] = {
      title: 'APS Report',
      ID: REPORTTYPE_BUYERS_APS,
      selected: false,
    };
  }

  if (publisher.ContextualSegmentEnabled === EBool.EBool_TRUE || isSuperUser) {
    reportTypes[REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS] = {
      title: 'Contextual Segment Report',
      ID: REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS,
      selected: false,
    };
  }

  // availaible only for SSAI
  if (
    publisher.NetworkPublisherMaps.length > 0 ||
    publisher.NetworkEnabled === EBool.EBool_TRUE ||
    isSuperUser
  ) {
    reportTypes[REPORTTYPE_STREAM] = {
      title: 'SSAI Report',
      ID: REPORTTYPE_STREAM,
      selected: false,
    };
    reportTypes[REPORTTYPE_STREAM_USER_ACTIVITY] = {
      title: 'SSAI User Activity Report',
      ID: REPORTTYPE_STREAM_USER_ACTIVITY,
      selected: false,
    };
  }

  reportTypes[REPORTTYPE_IAS_CONTENT_LEVEL_PERFORMANCE] = {
    title: 'IAS Content Level Performance',
    ID: REPORTTYPE_IAS_CONTENT_LEVEL_PERFORMANCE,
    selected: false,
  };

  // IAS third party reports
  if (publisher.EnableIASThirdPartyVASTTracking === EBool.EBool_TRUE || isSuperUser) {
    reportTypes[REPORTTYPE_IAS_THIRD_PARTY] = {
      title: 'IAS Third Party VAST Tracking',
      ID: REPORTTYPE_IAS_THIRD_PARTY,
      selected: false,
    };
  }

  // IAS first party reports
  if (
    (publisher.EnableIASFirstPartyVASTTracking === EBool.EBool_TRUE && publisher.IASAnID) ||
    isSuperUser
  ) {
    reportTypes[REPORTTYPE_IAS_FIRST_PARTY] = {
      title: 'IAS First Party VAST Tracking',
      ID: REPORTTYPE_IAS_FIRST_PARTY,
      selected: false,
    };
  }

  if (publisher.ID === PID.rarefiedAtmosphere || isSamsungPublisher(publisher.ID) || isSuperUser) {
    reportTypes[REPORTTYPE_CREATIVE_VALIDATION] = {
      title: 'Creative Validation Report',
      ID: REPORTTYPE_CREATIVE_VALIDATION,
      selected: false,
    };
  }

  if (isOpenPathEnabled(publisher) && isSuperUser) {
    reportTypes[REPORTTYPE_TTD] = {
      title: 'TTD Report *',
      ID: REPORTTYPE_TTD,
      selected: false,
    };
  }

  if (!isOttera()) {
    reportTypes[REPORTTYPE_CREATIVE_REVIEW] = {
      title: 'Creative Review Report',
      ID: REPORTTYPE_CREATIVE_REVIEW,
      selected: false,
    };
  }

  if (isIntegralplatform()) {
    const IASReportTypes = {} as IMenuList;

    IASReportTypes[REPORTTYPE_IAS_PERFORMANCE] = {
      title: 'Performance Report',
      ID: REPORTTYPE_IAS_PERFORMANCE,
      selected: false,
    };

    IASReportTypes[REPORTTYPE_IAS_DEVICE] = {
      title: 'Device Report',
      ID: REPORTTYPE_IAS_DEVICE,
      selected: false,
    };

    IASReportTypes[REPORTTYPE_IAS_YOUTUBE] = {
      title: 'Youtube Report',
      ID: REPORTTYPE_IAS_YOUTUBE,
      selected: false,
    };

    return IASReportTypes;
  }

  // TODO: remove if condition when Reporting Beta goes full release
  if (isSuperUser) {
    reportTypes[ReportTypeID.AuctionQuickReport] = {
      title: 'Auction Quick Report',
      ID: String(ReportTypeID.AuctionQuickReport),
      selected: false,
    };
    reportTypes[ReportTypeID.BidderQuickReport] = {
      title: 'Bidder Quick Report',
      ID: String(ReportTypeID.BidderQuickReport),
      selected: false,
    };
    reportTypes[ReportTypeID.CampaignQuickReport] = {
      title: 'Campaign Quick Report',
      ID: String(ReportTypeID.CampaignQuickReport),
      selected: false,
    };
    reportTypes[ReportTypeID.AdomainQuickReport] = {
      title: 'Adomain Quick Report',
      ID: String(ReportTypeID.AdomainQuickReport),
      selected: false,
    };
    reportTypes[ReportTypeID.ContentQuickReport] = {
      title: 'Content Quick Report',
      ID: String(ReportTypeID.ContentQuickReport),
      selected: false,
    };
    reportTypes[ReportTypeID.AppBundleQuickReport] = {
      title: 'App Bundle Quick Report',
      ID: String(ReportTypeID.AppBundleQuickReport),
      selected: false,
    };
  }

  return reportTypes;
};

/**
 * generateTrueFalseMenuList is used to generate IMenuList for filters that only are true or false.
 * @returns IMenuList of "true" and "false" for use in MUI dropdown
 */
export const generateTrueFalseMenuList = (): IMenuList => {
  const tfList: IMenuList = {};
  tfList.False = {
    ID: 'False',
    selected: false,
    title: 'False',
  };
  tfList.True = {
    ID: 'True',
    selected: false,
    title: 'True',
  };
  return tfList;
};

export const frequency: ISelectModel[] = [
  {
    key: FrequencyDaily,
    text: 'Daily',
  },
  {
    key: FrequencyWeekly,
    text: 'Weekly',
  },
  {
    key: FrequencyMonthly,
    text: 'Monthly',
  },
];

export const period: ISelectModel[] = [
  { key: '1', text: 'Last Day' },
  { key: '2', text: 'Last 3 Days' },
  { key: '3', text: 'Last 7 Days' },
  { key: '4', text: 'Last 15 Days' },
  { key: '8', text: 'Last 31 Days' },
  { key: '5', text: 'Current Month' },
  { key: '6', text: 'Last Month' },
  { key: '7', text: 'Current Year' },
];

export const scheduledDayWeekly: ISelectModel[] = [
  { key: '1', text: 'Monday' },
  { key: '2', text: 'Tuesday' },
  { key: '3', text: 'Wednesday' },
  { key: '4', text: 'Thursday' },
  { key: '5', text: 'Friday' },
  { key: '6', text: 'Saturday' },
  { key: '7', text: 'Sunday' },
];

export const scheduledDayMonthly: ISelectModel[] = (function () {
  const keyTextPairs = [];

  for (let i = 1; i < 32; i += 1) {
    keyTextPairs.push({
      key: '' + i,
      text: '' + i,
    });
  }

  return keyTextPairs;
})();

export interface IColumn {
  name: string;
  header: string;
  width: number;
}

function colName(v: string): string {
  return v.replace(/[ _-]/g, '').toLocaleLowerCase();
}

function mapColumns(cols: IColumn[]): { [key: string]: IColumn } {
  const ma: { [key: string]: IColumn } = {};
  cols.forEach((col: IColumn) => {
    ma[colName(col.name)] = col;
  });
  return ma;
}

const shareOfVoiceGroupBys = [
  { name: 'ContentChannel', header: 'Content Channel', width: 250 },
  { name: 'ContentNetwork', header: 'Content Network', width: 250 },
  { name: 'Day', header: 'Day', width: 250 },
  { name: 'Hour', header: 'Hour', width: 250 },
  { name: 'IsFallbackResponse', header: 'Is Fallback Response', width: 250 },
  { name: 'Livestream', header: 'Livestream', width: 200 },
  { name: 'Month', header: 'Month', width: 250 },
  { name: 'PhiloFallback', header: 'SOV Parallel Auction Type', width: 250 },
  { name: 'SiteID', header: 'Channel ID', width: 250 },
  { name: 'SiteName', header: 'Channel Name', width: 250 },
  { name: 'SOVInventoryType', header: 'SOV Inventory Type', width: 250 },
];

export const auctionGroupByListStandard: string[] = [
  'date',
  'hour',
  'day',
  'month',
  'week_of_year',
  'bidder_testing',
  'ad_break_position',
  'ad_duration',
  'apk_build',
  'app_bundle_id',
  'channel_group',
  'content_id',
  'country',
  'date_time',
  'delivered_ads',
  'destination_id',
  'destination_name',
  'device_type',
  'endpoint_error_code',
  'filled_ads',
  'inventory_route_id',
  'inventory_route_name',
  'ip_type',
  'is_bid_saver',
  'livestream',
  'pod_duration',
  'publisher_id',
  'samsung_model',
  'site_id',
  'site_name',
  'siteid',
  'sitename',
  'site_page',
  'targeting_device',
  'channel_label',
  'global_timeout',
];
export const auctionGroupByListExtendedOnly: string[] = [
  // endpoint blocked code
  'event_endpoint_blocked_code',
  // content related fields
  'content_cat',
  'content_channel',
  'content_context',
  'content_dist_id',
  'content_dist_name',
  'content_episode',
  'content_genre',
  'content_keywords',
  'content_language',
  'content_length',
  'content_network',
  'content_rating',
  'content_season',
  'content_series',
  'content_title',
  'content_prodqual',
  'content_producer_id',
  'content_producer_name',
  // custom related fields
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
  'custom11',
  'custom12',
  'custom13',
  'custom14',
  'custom15',
  'custom16',
  'custom17',
  'custom18',
  // geo related fields
  'dma',
  'city',
  'zip',
  // vmap specific field
  'share_type',
  // multi bid / podded request specific fields
  'is_multi_bid',
  'is_podded_request',
  'number_of_ads_requested',
  'iris_context',
  'iris_id',

  // samsung pubs only
  'has_google_pal_sdk_givn_param',
];
export const auctionGroupByListFastOnly: string[] = [
  'app_name',
  'coppa',
  'custom_targeting',
  'do_not_track',
  'fw_section_name',
  'fw_section_id_v2',
  'gdpr',
  'gdpr_consent',
  'operating_system',
  'ortb_site_name',
  'state',
  'us_privacy',
  'vast_error_code',
];
export const auctionGroupByListWithAdomainOnly: string[] = ['adomain'];
export const auctionGroupByListWithDemandPathOnly: string[] = ['demand_path', 'deal_name'];
export const auctionGroupByListFast = auctionGroupByListStandard.concat(auctionGroupByListFastOnly);
export const auctionGroupByListWithAdomain = auctionGroupByListStandard.concat(
  auctionGroupByListWithAdomainOnly,
);
export const auctionGroupByListWithDemandPath = auctionGroupByListStandard.concat(
  auctionGroupByListWithDemandPathOnly,
);

export const buyerGroupByListStandard: string[] = [
  'date',
  'hour',
  'day',
  'month',
  'week_of_year',
  'bidder_testing',
  'ad_break_position',
  'ad_duration',
  'apk_build',
  'app_bundle_id',
  'bid_error_code',
  'bid_won_reject_reason',
  'channel_group',
  'content_id',
  'country',
  'date_time',
  'delivered_ads',
  'demand_side_platform_id',
  'destination_id',
  'destination_name',
  'device_type',
  'filled_ads',
  'inventory_route_id',
  'inventory_route_name',
  'ip_type',
  'is_bid_saver',
  'livestream',
  'pod_duration',
  'publisher_id',
  'country_group',
  'samsung_model',
  'share_of_voice',
  'site_id',
  'site_name',
  'siteid',
  'sitename',
  'site_page',
  'targeting_device',
  // dooh specific fields
  'venue_id',
  'channel_label',
  'global_timeout',
  'bidder_label',
];
export const buyerGroupByListExtendedOnly: string[] = [
  'advertiser_id',
  'advertiser_name',
  'ad_source_id',
  // misc
  'bid_response_deal_id',
  // content related fields
  'content_cat',
  'content_channel',
  'content_context',
  'content_dist_id',
  'content_dist_name',
  'content_episode',
  'content_genre',
  'content_keywords',
  'content_language',
  'content_length',
  'content_network',
  'content_rating',
  'content_season',
  'content_series',
  'content_title',
  'content_prodqual',
  'content_producer_id',
  'content_producer_name',
  // custom related fields
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
  'custom11',
  'custom12',
  'custom13',
  'custom14',
  'custom15',
  'custom16',
  'custom17',
  'custom18',
  // geo related fields
  'dma',
  'city',
  'zip',
  // podded request specific fields
  'is_multi_bid',
  'is_podded_request',
  'number_of_ads_requested',
  'iris_context',
  'iris_id',

  // samsung pubs only
  'has_google_pal_sdk_givn_param',
  'buyer_id',
];
export const buyerGroupByListFastOnly: string[] = [
  'active_bidder_tier',
  'bidder_tier',
  'bidder_subtier',
  'brand_safety_rule_id',
  'coppa',
  'custom_targeting',
  'do_not_track',
  'fw_section_name',
  'fw_section_id_v2',
  'gdpr',
  'gdpr_consent',
  'operating_system',
  'ortb_site_name',
  'philo_fallback',
  'state',
  'us_privacy',
  'vast_error_code',
];
export const buyerGroupByListWithAdomainOnly: string[] = ['adomain'];
export const buyerGroupByListWithDemandPathOnly: string[] = [
  'demand_path',
  'deal_name',
  'dsp_name',
];
export const buyerGroupByListFast = buyerGroupByListStandard.concat(buyerGroupByListFastOnly);
export const buyerGroupByListWithAdomain = buyerGroupByListStandard.concat(
  buyerGroupByListWithAdomainOnly,
);
export const buyerGroupByListWithDemandPath = buyerGroupByListStandard.concat(
  buyerGroupByListWithDemandPathOnly,
);

const getColumnGroupingByReportIDWithPublisherID = (
  publisherID: string,
): { [key: string]: IColumn[] } => {
  // Note: When you add to an extended table, make sure you also add to buyerGroupByListExtendedOnly / auctionExtendedGroupByParams in graph.ts
  // to make sure the right graph is queried
  const columnGroupingByReportID: { [key: string]: IColumn[] } = {
    2: [
      // auctionGroupBys
      { name: 'AdBreakPosition', header: 'Ad Break Position', width: 250 },
      { name: 'AdDuration', header: 'Ad Duration', width: 250 },
      { name: 'Adomain', header: 'Adomain', width: 250 },
      { name: 'AppBundleID', header: 'App Bundle ID', width: 250 },
      { name: 'AppName', header: 'App Name', width: 200 },
      { name: 'BidderTesting', header: 'Bidder Testing', width: 200 },
      { name: 'IsBidSaver', header: 'Publica Vault', width: 200 },
      { name: 'IsMultiBid', header: 'Multi Bid', width: 200 },
      { name: 'IsPoddedRequest', header: 'Podded Request', width: 200 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'City', header: 'City', width: 200 },
      { name: 'ContentCat', header: 'Content Cat', width: 250 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentContext', header: 'Content Context', width: 250 },
      { name: 'ContentDistID', header: 'Content Dist. ID', width: 250 },
      { name: 'ContentDistName', header: 'Content Dist. Name', width: 250 },
      { name: 'ContentEpisode', header: 'Content Episode', width: 250 },
      { name: 'ContentGenre', header: 'Content Genre', width: 250 },
      { name: 'ContentID', header: 'Content ID', width: 250 },
      { name: 'ContentKeywords', header: 'Content Keywords', width: 250 },
      { name: 'ContentLanguage', header: 'Content Language', width: 250 },
      { name: 'ContentLength', header: 'Content Length', width: 250 },
      { name: 'ContentNetwork', header: 'Content Network', width: 250 },
      { name: 'ContentProdQual', header: 'Content Prod. Qual.', width: 250 },
      { name: 'ContentProducerID', header: 'Content Producer ID', width: 200 },
      { name: 'ContentProducerName', header: 'Content Producer Name', width: 200 },
      { name: 'ContentRating', header: 'Content Rating', width: 250 },
      { name: 'ContentSeason', header: 'Content Season', width: 250 },
      { name: 'ContentSeries', header: 'Content Series', width: 250 },
      { name: 'ContentTitle', header: 'Content Title', width: 200 },
      { name: 'Coppa', header: 'COPPA', width: 250 },
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'Custom4', header: 'Custom4', width: 200 },
      { name: 'Custom5', header: 'Custom5', width: 200 },
      { name: 'Custom6', header: 'Custom6', width: 200 },
      { name: 'Custom7', header: 'Custom7', width: 200 },
      { name: 'Custom8', header: 'Custom8', width: 200 },
      { name: 'Custom9', header: 'Custom9', width: 200 },
      { name: 'Custom10', header: 'Custom10', width: 200 },
      { name: 'Custom11', header: 'Custom11', width: 200 },
      { name: 'Custom12', header: 'Custom12', width: 200 },
      { name: 'Custom13', header: 'Custom13', width: 200 },
      { name: 'Custom14', header: 'Custom14', width: 200 },
      { name: 'Custom15', header: 'Custom15', width: 200 },
      { name: 'Custom16', header: 'Custom16', width: 200 },
      { name: 'Custom17', header: 'Custom17', width: 200 },
      { name: 'Custom18', header: 'Custom18', width: 200 },
      { name: 'CustomTargeting', header: 'Custom Targeting', width: 200 },
      { name: 'DMA', header: 'DMA', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DealName', header: 'Deal Name', width: 250 },
      { name: 'DedupBypass', header: 'Dedup Bypass', width: 200 },
      { name: 'DemandPath', header: 'Demand Path', width: 200 },
      { name: 'DestinationID', header: 'Destination ID', width: 250 },
      { name: 'DestinationName', header: 'Destination Name', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'DeviceSubType', header: 'Device Subtype', width: 250 },
      { name: 'DoNotTrack', header: 'Do Not Track', width: 200 },
      { name: 'EndpointErrorCode', header: 'Endpoint Error Type', width: 250 },
      { name: 'EventEndpointBlockedCode', header: 'Endpoint Blocked Reason', width: 250 },
      { name: 'FilledAds', header: 'Filled Ads Per Pod', width: 200 },
      { name: 'FwSectionName', header: 'Freewheel Section Name', width: 250 },
      { name: 'FwSectionIdV2', header: 'Freewheel Section ID', width: 250 },
      { name: 'DeliveredAds', header: 'Delivered Ads Per Pod', width: 200 },
      { name: 'Gdpr', header: 'GDPR', width: 200 },
      { name: 'GdprConsent', header: 'GDPR Consent', width: 200 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'InventoryRouteID', header: 'Inventory Route ID', width: 250 },
      { name: 'InventoryRouteName', header: 'Inventory Route Name', width: 250 },
      { name: 'IPType', header: 'IP Type', width: 200 },
      { name: 'IrisContext', header: 'Iris Context', width: 200 },
      { name: 'IrisID', header: 'Iris ID', width: 200 },
      { name: 'Livestream', header: 'Livestream', width: 200 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'NumberOfAdsRequested', header: 'Num Ads Requested', width: 200 },
      { name: 'ORTBSiteName', header: 'Site Name', width: 250 },
      { name: 'PodDuration', header: 'Pod Duration', width: 200 },
      { name: 'PublisherName', header: 'Publisher Name', width: 200 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'SitePage', header: 'Site Page', width: 250 },
      { name: 'State', header: 'State', width: 200 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 200 },
      { name: 'UsPrivacy', header: 'US Privacy', width: 200 },
      { name: 'WeekOfYear', header: 'Week Of Year', width: 200 },
      { name: 'Zip', header: 'Zip', width: 200 },
      { name: 'ChannelLabel', header: 'Channel Label', width: 200 },
      { name: 'GlobalTimeout', header: 'Global Timeout', width: 200 },
    ],
    3: [
      // bidderGroupBys
      { name: 'ActiveBidderTier', header: 'Active Bidder Tier', width: 200 },
      { name: 'AdBreakPosition', header: 'Ad Break Position', width: 250 },
      { name: 'AdDuration', header: 'Ad Duration', width: 250 },
      { name: 'Adomain', header: 'Adomain', width: 250 },
      { name: 'AdSourceID', header: 'Ad Source ID', width: 250 },
      { name: 'AppBundleID', header: 'App Bundle ID', width: 250 },
      { name: 'AppName', header: 'App Name', width: 250 },
      { name: 'BidDensity', header: 'Bid Density', width: 300 },
      { name: 'IsBidSaver', header: 'Publica Vault', width: 200 },
      { name: 'IsMultiBid', header: 'Multi Bid', width: 200 },
      { name: 'IsPoddedRequest', header: 'Podded Request', width: 200 },
      { name: 'BidErrorCode', header: 'Error Type', width: 300 },
      { name: 'BidResponseDealID', header: 'Bid Response Deal ID', width: 300 },
      { name: 'BidderGroup', header: 'Bidder Group', width: 250 },
      { name: 'BidderID', header: 'Bidder ID', width: 250 },
      { name: 'BidderLabel', header: 'Bidder Label', width: 200 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'BidderTier', header: 'Bidder Tier', width: 200 },
      { name: 'BidderTesting', header: 'Bidder Testing', width: 200 },
      { name: 'BidderType', header: 'Demand Source', width: 200 },
      { name: 'BidWonRejectReason', header: 'Bid Reject Reason', width: 300 },
      { name: 'BrandSafetyRuleID', header: 'Brand Safety Rule ID', width: 200 },
      { name: 'Category', header: 'IAB Category', width: 350 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'City', header: 'City', width: 200 },
      { name: 'ContentCat', header: 'Content Cat', width: 250 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentContext', header: 'Content Context', width: 250 },
      { name: 'ContentDistID', header: 'Content Dist. ID', width: 250 },
      { name: 'ContentDistName', header: 'Content Dist. Name', width: 250 },
      { name: 'ContentEpisode', header: 'Content Episode', width: 250 },
      { name: 'ContentGenre', header: 'Content Genre', width: 250 },
      { name: 'ContentID', header: 'Content ID', width: 250 },
      { name: 'ContentKeywords', header: 'Content Keywords', width: 250 },
      { name: 'ContentLanguage', header: 'Content Language', width: 250 },
      { name: 'ContentLength', header: 'Content Length', width: 250 },
      { name: 'ContentNetwork', header: 'Content Network', width: 250 },
      { name: 'ContentProdQual', header: 'Content Prod. Qual.', width: 250 },
      { name: 'ContentProducerID', header: 'Content Producer ID', width: 200 },
      { name: 'ContentProducerName', header: 'Content Producer Name', width: 200 },
      { name: 'ContentRating', header: 'Content Rating', width: 250 },
      { name: 'ContentSeason', header: 'Content Season', width: 250 },
      { name: 'ContentSeries', header: 'Content Series', width: 250 },
      { name: 'ContentTitle', header: 'Content Title', width: 200 },
      { name: 'Coppa', header: 'COPPA', width: 250 },
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'Custom1', header: 'Slot #', width: 200 },
      { name: 'Custom4', header: 'Custom4', width: 200 },
      { name: 'Custom5', header: 'Custom5', width: 200 },
      { name: 'Custom6', header: 'Custom6', width: 200 },
      { name: 'Custom7', header: 'Custom7', width: 200 },
      { name: 'Custom8', header: 'Custom8', width: 200 },
      { name: 'Custom9', header: 'Custom9', width: 200 },
      { name: 'Custom10', header: 'Custom10', width: 200 },
      { name: 'Custom11', header: 'Custom11', width: 200 },
      { name: 'Custom12', header: 'Custom12', width: 200 },
      { name: 'Custom13', header: 'Custom13', width: 200 },
      { name: 'Custom14', header: 'Custom14', width: 200 },
      { name: 'Custom15', header: 'Custom15', width: 200 },
      { name: 'Custom16', header: 'Custom16', width: 200 },
      { name: 'Custom17', header: 'Custom17', width: 200 },
      { name: 'Custom18', header: 'Custom18', width: 200 },
      { name: 'CustomTargeting', header: 'Custom Targeting', width: 200 },
      { name: 'DMA', header: 'DMA', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DealID', header: 'Deal ID', width: 200 },
      { name: 'DealName', header: 'Deal Name', width: 250 },
      { name: 'DedupBypass', header: 'Dedup Bypass', width: 200 },
      { name: 'DemandPath', header: 'Demand Path', width: 200 },
      { name: 'DemandSidePlatformID', header: 'Demand Side Platform ID', width: 200 },
      { name: 'DestinationID', header: 'Destination ID', width: 200 },
      { name: 'DestinationName', header: 'Destination Name', width: 200 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'DeviceSubType', header: 'Device Subtype', width: 250 },
      { name: 'DoNotTrack', header: 'Do Not Track', width: 200 },
      { name: 'FwSectionName', header: 'Freewheel Section Name', width: 250 },
      { name: 'FwSectionIdV2', header: 'Freewheel Section ID', width: 250 },
      { name: 'Gdpr', header: 'GDPR', width: 200 },
      { name: 'GdprConsent', header: 'GDPR Consent', width: 200 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'InventoryRouteID', header: 'Inventory Route ID', width: 200 },
      { name: 'InventoryRouteName', header: 'Inventory Route Name', width: 200 },
      { name: 'IPType', header: 'IP Type', width: 200 },
      { name: 'IrisContext', header: 'Iris Context', width: 200 },
      { name: 'IrisID', header: 'Iris ID', width: 200 },
      { name: 'Livestream', header: 'Livestream', width: 200 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'ORTBSiteName', header: 'Site Name', width: 250 },
      { name: 'PodDuration', header: 'Pod Duration', width: 200 },
      { name: 'PublisherName', header: 'Publisher Name', width: 200 },
      { name: 'ShareOfVoice', header: 'Share of Voice', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'SitePage', header: 'Site Page', width: 250 },
      { name: 'SpotxEndpoint', header: 'Spotx Endpoint', width: 250 },
      { name: 'SpotxParamChannelID', header: 'Spotx Channel ID', width: 250 },
      { name: 'State', header: 'State', width: 200 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 200 },
      { name: 'UsPrivacy', header: 'US Privacy', width: 200 },
      { name: 'VastErrorCode', header: 'Vast Error Type', width: 250 },
      { name: 'VenueID', header: 'Venue ID', width: 250 },
      { name: 'WeekOfYear', header: 'Week Of Year', width: 200 },
      { name: 'Zip', header: 'Zip', width: 200 },
      { name: 'ChannelLabel', header: 'Channel Label', width: 200 },
      { name: 'GlobalTimeout', header: 'Global Timeout', width: 200 },
      { name: 'DspName', header: 'DSP', width: 200 },
    ],
    4: [
      // SSP
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'BidderType', header: 'Bidder Type', width: 200 },
      { name: 'BidErrorCode', header: 'Error Type', width: 300 },
      { name: 'BidDensity', header: 'Bid Density', width: 300 },
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'Custom1', header: 'Slot #', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'PublisherName', header: 'Publisher Name', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
    ],
    5: [
      // Campaign Report
      { name: 'Adomain', header: 'Adomain', width: 250 },
      { name: 'OrderAlias', header: 'Order Alias', width: 250 },
      { name: 'LineItemAlias', header: 'Line Item Alias', width: 250 },
      { name: 'AppBundleID', header: 'App Bundle ID', width: 250 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'CampaignName', header: 'Line Item Name', width: 250 },
      { name: 'Category', header: 'IAB Category', width: 350 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'City', header: 'City', width: 200 },
      { name: 'ContentCat', header: 'Content Cat', width: 250 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentContext', header: 'Content Context', width: 250 },
      { name: 'ContentDistID', header: 'Content Dist. ID', width: 250 },
      { name: 'ContentDistName', header: 'Content Dist. Name', width: 250 },
      { name: 'ContentEpisode', header: 'Content Episode', width: 250 },
      { name: 'ContentGenre', header: 'Content Genre', width: 250 },
      { name: 'ContentID', header: 'Content ID', width: 250 },
      { name: 'ContentKeywords', header: 'Content Keywords', width: 250 },
      { name: 'ContentLanguage', header: 'Content Language', width: 250 },
      { name: 'ContentLength', header: 'Content Length', width: 250 },
      { name: 'ContentNetwork', header: 'Content Network', width: 250 },
      { name: 'ContentProdQual', header: 'Content Prod. Qual.', width: 250 },
      { name: 'ContentProducerID', header: 'Content Producer ID', width: 200 },
      { name: 'ContentProducerName', header: 'Content Producer Name', width: 200 },
      { name: 'ContentRating', header: 'Content Rating', width: 250 },
      { name: 'ContentSeason', header: 'Content Season', width: 250 },
      { name: 'ContentSeries', header: 'Content Series', width: 250 },
      { name: 'ContentTitle', header: 'Content Title', width: 200 },
      { name: 'Coppa', header: 'COPPA', width: 250 },
      { name: 'Country', header: 'Country', width: 200 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'CreativeID', header: 'Creative ID', width: 200 },
      { name: 'CreativeName', header: 'Creative Name', width: 200 },
      { name: 'Custom4', header: 'Custom4', width: 200 },
      { name: 'Custom5', header: 'Custom5', width: 200 },
      { name: 'Custom6', header: 'Custom6', width: 200 },
      { name: 'Custom7', header: 'Custom7', width: 200 },
      { name: 'Custom8', header: 'Custom8', width: 200 },
      { name: 'Custom9', header: 'Custom9', width: 200 },
      { name: 'Custom10', header: 'Custom10', width: 200 },
      { name: 'Custom11', header: 'Custom11', width: 200 },
      { name: 'Custom12', header: 'Custom12', width: 200 },
      { name: 'Custom13', header: 'Custom13', width: 200 },
      { name: 'Custom14', header: 'Custom14', width: 200 },
      { name: 'Custom15', header: 'Custom15', width: 200 },
      { name: 'Custom16', header: 'Custom16', width: 200 },
      { name: 'Custom17', header: 'Custom17', width: 200 },
      { name: 'Custom18', header: 'Custom18', width: 200 },
      { name: 'CustomTargeting', header: 'Custom Targeting', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'DeviceSubType', header: 'Device Subtype', width: 250 },
      { name: 'DMA', header: 'DMA', width: 200 },
      { name: 'DoNotTrack', header: 'Do Not Track', width: 200 },
      { name: 'FlightDateCheckpointID', header: 'Flight Date Checkpoint ID', width: 250 },
      { name: 'FlightDateCheckpointName', header: 'Flight Date Checkpoint Name', width: 250 },
      { name: 'Gdpr', header: 'GDPR', width: 200 },
      { name: 'GdprConsent', header: 'GDPR Consent', width: 200 },
      { name: 'GlobalMediafileID', header: 'Global Mediafile ID', width: 200 },
      { name: 'GlobalVastID', header: 'Global Vast ID', width: 200 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Livestream', header: 'Livestream', width: 200 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'NeedsCreative', header: 'Line Item Needs Creative', width: 200 },
      { name: 'NoFillReason', header: 'No Fill Reason', width: 200 },
      { name: 'PublisherName', header: 'Publisher Name', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'SitePage', header: 'Site Page', width: 250 },
      { name: 'StartDate', header: 'Line Item Start Date', width: 200 },
      { name: 'State', header: 'State', width: 200 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 200 },
      { name: 'UsPrivacy', header: 'US Privacy', width: 200 },
      { name: 'WeekOfYear', header: 'Week Of Year', width: 200 },
      { name: 'Zip', header: 'Zip', width: 200 },

      // line item map
      { name: 'AdvertiserName', header: 'Advertiser Name', width: 200 },
      { name: 'AgencyName', header: 'Agency Name', width: 200 },
      { name: 'LineItemStatus', header: 'Line Item Status', width: 200 },
      { name: 'LineItemID', header: 'Line Item ID', width: 200 },
      { name: 'OrderID', header: 'Order ID', width: 200 },
      { name: 'OrderName', header: 'Order Name', width: 200 },
      { name: 'SalespersonName', header: 'Salesperson Name', width: 200 },
      { name: 'TraffickerName', header: 'Trafficker Name', width: 200 },
    ],
    6: [
      // Audience Segments
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'PublisherName', header: 'Publisher Name', width: 250 },
      { name: 'SegmentName', header: 'Segment Names', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
    ],
    7: [
      // SSAI Report Groupings
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'NetworkID', header: 'Network ID', width: 250 },
      { name: 'NetworkName', header: 'Network Name', width: 250 },
      { name: 'NetworkChannelID', header: 'Network Channel ID', width: 250 },
      { name: 'NetworkChannelName', header: 'Network Channel Name', width: 250 },
      { name: 'NetworkChannelSiteID', header: 'Network Channel Site ID', width: 250 },
      { name: 'SlotNumber', header: 'Slot Number', width: 250 },
      { name: 'NetworkChannelSiteName', header: 'Network Channel Site Name', width: 250 },
      { name: 'StreamErrorCode', header: 'Stream Error Code', width: 250 },
      { name: 'DeviceGeoRegion', header: 'Device Geo Region', width: 250 },
      { name: 'DeviceMake', header: 'Device Make', width: 250 },
      { name: 'DeviceModel', header: 'Device Model', width: 250 },
      { name: 'DeviceOS', header: 'Device OS', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'Livestream', header: 'Livestream (Stream Type)', width: 250 },
      { name: 'ContentID', header: 'Content ID', width: 250 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'StreamFormatType', header: 'Stream Format Type', width: 250 },
      { name: 'IsPartialAdBreak', header: 'Is Partial Ad Break', width: 250 },
    ],
    8: [
      // Unique User Report Day, Month, Device Environment, Channel Name, ID Type
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DMA', header: 'DMA', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'PublisherName', header: 'Publisher Name', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'State', header: 'State', width: 250 },
      { name: 'SyntheticID', header: 'Device ID Type', width: 250 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 250 },
      { name: 'DealID', header: 'Deal ID', width: 200 },
      // preset aggregates
      { name: 'PresetAggregateForYesterday', header: 'Yesterday', width: 250 },
      { name: 'PresetAggregateForLastSevenDays', header: 'Last 7 Days', width: 250 },
      { name: 'PresetAggregateForLastThirtyDays', header: 'Last 30 Days', width: 250 },
      { name: 'WeekOfYear', header: 'Week Of Year', width: 200 },
    ],
    9: [
      // Audience Segments Superuser For All Publisher
      { name: 'DataType', header: 'Data Type', width: 250 },
      { name: 'DataProvider', header: 'Data Provider', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'PublisherName', header: 'Publisher Name', width: 250 },
      { name: 'SegmentName', header: 'Segment Names', width: 250 },
      { name: 'LiverampSegmentIDs', header: 'Liveramp Audience ID', width: 250 },
      { name: 'LRDmProviders', header: 'LR DM Providers', width: 250 },
      { name: 'LRDmSegIDs', header: 'LR DM Segment IDs', width: 250 },
      { name: 'LRDmDataFee', header: 'LR Matching Fee', width: 250 },
      { name: 'LR1pMatchingFee', header: 'LR 1P Matching Fee', width: 250 },
      { name: 'LR3pMatchingFee', header: 'LR 3P Matching Fee', width: 250 },
    ],
    10: [
      // Auction Fill Rate
      { name: 'Day', header: 'Day', width: 200 },
      { name: 'PodDuration', header: 'Pod Duration', width: 200 },
      { name: 'ImpressedBidderType', header: 'Demand Partner', width: 200 },
    ],
    13: [
      // Brand Safety Tracker
      { name: 'AdDuration', header: 'Ad Duration', width: 250 },
      { name: 'AppBundleID', header: 'App Bundle ID', width: 250 },
      { name: 'AppName', header: 'App Name', width: 250 },
      { name: 'BidderGroup', header: 'Bidder Group', width: 250 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'BidderType', header: 'Demand Source', width: 200 },
      { name: 'BrandSafetyTrackerName', header: 'Brand Safety Tracker Name', width: 350 },
      { name: 'BrandSafetyTrackerType', header: 'Brand Safety Tracker Type', width: 350 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'IPType', header: 'IP Type', width: 200 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'PublisherName', header: 'Publisher Name', width: 200 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'SitePage', header: 'Site Page', width: 250 },
    ],
    14: [
      // Deals
      { name: 'BidErrorCode', header: 'Error Type', width: 300 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'BidderType', header: 'Demand Source', width: 200 },
      { name: 'BidWonRejectReason', header: 'Bid Won Reject Reason', width: 300 },
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'FwSectionName', header: 'Freewheel Section Name', width: 250 },
      { name: 'FwSectionIdV2', header: 'Freewheel Section ID', width: 250 },
      { name: 'InboundDealId', header: 'Inbound Deal ID', width: 250 },
      { name: 'InboundWseat', header: 'Inbound WSeat', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'ORTBSiteName', header: 'Site Name', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
    ],
    15: [
      // Publica Deals
      { name: 'BidErrorCode', header: 'Error Type', width: 300 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'BidderType', header: 'Demand Source', width: 200 },
      { name: 'BidWonRejectReason', header: 'Bid Won Reject Reason', width: 300 },
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'ORTBSiteName', header: 'Site Name', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'OutboundDealId', header: 'Outbound Deal ID', width: 250 },
    ],
    16: [
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
    ],
    17: [
      // inventory routes
      { name: 'AppBundleID', header: 'App Bundle ID', width: 250 },
      { name: 'AppName', header: 'App Name', width: 250 },
      { name: 'City', header: 'City', width: 200 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentDistID', header: 'Content Dist. ID', width: 250 },
      { name: 'ContentDistName', header: 'Content Dist. Name', width: 250 },
      { name: 'ContentGenre', header: 'Content Genre', width: 250 },
      { name: 'ContentID', header: 'Content ID', width: 250 },
      { name: 'ContentProducerID', header: 'Content Producer ID', width: 200 },
      { name: 'ContentProducerName', header: 'Content Producer Name', width: 200 },
      { name: 'ContentRating', header: 'Content Rating', width: 250 },
      { name: 'ContentSeries', header: 'Content Series', width: 250 },
      { name: 'ContentTitle', header: 'Content Title', width: 200 },
      { name: 'Coppa', header: 'COPPA', width: 250 },
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'Custom4', header: 'Custom4', width: 200 },
      { name: 'Custom5', header: 'Custom5', width: 200 },
      { name: 'Custom6', header: 'Custom6', width: 200 },
      { name: 'Custom7', header: 'Custom7', width: 200 },
      { name: 'Custom8', header: 'Custom8', width: 200 },
      { name: 'Custom9', header: 'Custom9', width: 200 },
      { name: 'Custom10', header: 'Custom10', width: 200 },
      { name: 'Custom11', header: 'Custom11', width: 200 },
      { name: 'Custom12', header: 'Custom12', width: 200 },
      { name: 'Custom13', header: 'Custom13', width: 200 },
      { name: 'Custom14', header: 'Custom14', width: 200 },
      { name: 'Custom15', header: 'Custom15', width: 200 },
      { name: 'Custom16', header: 'Custom16', width: 200 },
      { name: 'Custom17', header: 'Custom17', width: 200 },
      { name: 'Custom18', header: 'Custom18', width: 200 },
      { name: 'DMA', header: 'DMA', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'DeviceSubType', header: 'Device Subtype', width: 250 },
      { name: 'DoNotTrack', header: 'Do Not Track', width: 200 },
      { name: 'Gdpr', header: 'GDPR', width: 200 },
      { name: 'GdprConsent', header: 'GDPR Consent', width: 200 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'InventoryRouteID', header: 'Inventory Route ID', width: 250 },
      { name: 'InventoryRouteName', header: 'Inventory Route Name', width: 250 },
      { name: 'InventoryRouteCondition', header: 'Inventory Route Condition', width: 250 },
      { name: 'InventoryRouteErrorCode', header: 'Inventory Route Error Code', width: 250 },
      {
        name: 'InventoryRouteResponseDestinationID',
        header: 'Inventory Route Response Destination ID',
        width: 250,
      },
      { name: 'Livestream', header: 'Livestream', width: 200 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'State', header: 'State', width: 200 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 200 },
      { name: 'UsPrivacy', header: 'US Privacy', width: 200 },
      { name: 'Zip', header: 'Zip', width: 200 },
    ],
    18: [
      // destinations
      { name: 'AppBundleID', header: 'App Bundle ID', width: 250 },
      { name: 'AppName', header: 'App Name', width: 250 },
      { name: 'City', header: 'City', width: 200 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentDistID', header: 'Content Dist. ID', width: 250 },
      { name: 'ContentDistName', header: 'Content Dist. Name', width: 250 },
      { name: 'ContentGenre', header: 'Content Genre', width: 250 },
      { name: 'ContentID', header: 'Content ID', width: 250 },
      { name: 'ContentProducerID', header: 'Content Producer ID', width: 200 },
      { name: 'ContentProducerName', header: 'Content Producer Name', width: 200 },
      { name: 'ContentRating', header: 'Content Rating', width: 250 },
      { name: 'ContentSeries', header: 'Content Series', width: 250 },
      { name: 'ContentTitle', header: 'Content Title', width: 200 },
      { name: 'Coppa', header: 'COPPA', width: 250 },
      { name: 'Country', header: 'Country', width: 250 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'Custom4', header: 'Custom4', width: 200 },
      { name: 'Custom5', header: 'Custom5', width: 200 },
      { name: 'Custom6', header: 'Custom6', width: 200 },
      { name: 'Custom7', header: 'Custom7', width: 200 },
      { name: 'Custom8', header: 'Custom8', width: 200 },
      { name: 'Custom9', header: 'Custom9', width: 200 },
      { name: 'Custom10', header: 'Custom10', width: 200 },
      { name: 'Custom11', header: 'Custom11', width: 200 },
      { name: 'Custom12', header: 'Custom12', width: 200 },
      { name: 'Custom13', header: 'Custom13', width: 200 },
      { name: 'Custom14', header: 'Custom14', width: 200 },
      { name: 'Custom15', header: 'Custom15', width: 200 },
      { name: 'Custom16', header: 'Custom16', width: 200 },
      { name: 'Custom17', header: 'Custom17', width: 200 },
      { name: 'Custom18', header: 'Custom18', width: 200 },
      { name: 'DMA', header: 'DMA', width: 200 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DestinationID', header: 'Destination ID', width: 250 },
      { name: 'DestinationName', header: 'Destination Name', width: 250 },
      { name: 'DestinationErrorCode', header: 'Destination Error Code', width: 250 },
      { name: 'DestinationSiteID', header: 'Destination Site ID', width: 250 },
      { name: 'DestinationSiteName', header: 'Destination Site Name', width: 250 },
      { name: 'DestinationType', header: 'Destination Type', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'DeviceSubType', header: 'Device Subtype', width: 250 },
      { name: 'DoNotTrack', header: 'Do Not Track', width: 200 },
      { name: 'Gdpr', header: 'GDPR', width: 200 },
      { name: 'GdprConsent', header: 'GDPR Consent', width: 200 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'InventoryRouteID', header: 'Inventory Route ID', width: 250 },
      { name: 'InventoryRouteName', header: 'Inventory Route Name', width: 250 },
      { name: 'InventoryRouteCondition', header: 'Inventory Route Condition', width: 250 },
      { name: 'Livestream', header: 'Livestream', width: 200 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'State', header: 'State', width: 200 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 200 },
      { name: 'UsPrivacy', header: 'US Privacy', width: 200 },
      { name: 'Zip', header: 'Zip', width: 200 },
    ],
    19: [
      // Contextual Segments
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'AppliedContextualSegmentIDs', header: 'Applied Contextual Segment IDs', width: 250 },
      {
        name: 'AppliedContextualSegmentNames',
        header: 'Applied Contextual Segment Names',
        width: 250,
      },
      { name: 'IASContextualInfo', header: 'Contextual Info', width: 250 },
      { name: 'DealID', header: 'Deal ID', width: 100 },
      {
        name: 'AppliedContextualSegmentPartners',
        header: 'Applied Contextual Segment Partners',
        width: 250,
      },
      { name: 'ContentGenre', header: 'Content Genre', width: 200 },
      { name: 'ContentLength', header: 'Content Length', width: 200 },
      { name: 'ContentRating', header: 'Content Rating', width: 200 },
    ],
    20: [
      // IAS Third Party
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'PublicaChannelID', header: 'Publica Channel ID', width: 250 },
      { name: 'PublicaBidderID', header: 'Publica Bidder ID', width: 250 },
      { name: 'BidderGroup', header: 'Publica Bidder Group', width: 250 },
      { name: 'BidderType', header: 'Publica Demand Source', width: 250 },
      { name: 'PublisherID', header: 'Publisher ID', width: 250 },
      { name: 'PlacementID', header: 'Placement ID', width: 250 },
      { name: 'MediaTypeID', header: 'Media Type', width: 250 },
    ],
    21: [
      // IAS First Party
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'AdvertiserID', header: 'Advertiser ID', width: 250 },
      { name: 'AdvertiserName', header: 'Advertiser Name', width: 250 },
      { name: 'AdSize', header: 'Ad Size', width: 250 },
      { name: 'AdUnitID', header: 'Ad Unit ID', width: 250 },
      { name: 'AdUnitPath', header: 'Ad Unit Path', width: 250 },
      { name: 'LineItemID', header: 'Line Item ID', width: 250 },
      { name: 'LineItemName', header: 'Line Item Name', width: 250 },
      { name: 'OrderID', header: 'Order ID', width: 250 },
      { name: 'OrderName', header: 'Order Name', width: 250 },
      { name: 'CreativeID', header: 'Creative ID', width: 250 },
      { name: 'CreativeName', header: 'Creative Name', width: 250 },
      { name: 'MediaTypeID', header: 'Media Type', width: 250 },
    ],
    22: [
      // Creative Validation
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'Adomain', header: 'Adomain', width: 250 },
      { name: 'MediaFile', header: 'Media File', width: 250 },
    ],
    24: [
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
    ],
    23: [
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
    ],
    25: [
      // IAS Performance Report
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'AdvertiserID', header: 'Advertiser ID', width: 250 },
      { name: 'AdvertiserName', header: 'Advertiser Name', width: 250 },
      { name: 'AdSize', header: 'Ad Size', width: 250 },
      { name: 'AdUnitID', header: 'Ad Unit ID', width: 250 },
      { name: 'AdUnitPath', header: 'Ad Unit Path', width: 250 },
      { name: 'LineItemID', header: 'Line Item ID', width: 250 },
      { name: 'LineItemName', header: 'Line Item Name', width: 250 },
      { name: 'OrderID', header: 'Order ID', width: 250 },
      { name: 'OrderName', header: 'Order Name', width: 250 },
      { name: 'CreativeID', header: 'Creative ID', width: 250 },
      { name: 'CreativeName', header: 'Creative Name', width: 250 },
      { name: 'MediaTypeID', header: 'Media Type', width: 250 },
    ],
    26: [
      // IAS Device Report
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'MediaTypeID', header: 'Device Type', width: 250 },
    ],
    27: [
      // IAS Youtube Report
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'MediaTypeID', header: 'Device Type', width: 250 },
    ],
    28: [
      // TTD Report
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'CurrencyType', header: 'Currency Type', width: 250 },
      { name: 'Timezone', header: 'Timezone', width: 250 },
      { name: 'MarketType', header: 'Market Type', width: 250 },
      { name: 'MediaType', header: 'Media Type', width: 250 },
      { name: 'PublisherId', header: 'Publisher ID', width: 250 },
    ],
    29: [
      // Creative Review Report
      { name: 'Timestamp', header: 'Date of Change', width: 250 },
      { name: 'MediaFile', header: 'Media File', width: 500 },
    ],
    30: shareOfVoiceGroupBys,
    31: [
      // Pod Hourly Report
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'PublisherID', header: 'Publisher ID', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentNetwork', header: 'Content Network', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'IsBidSaver', header: 'Publica Vault', width: 250 },
      { name: 'PodCompareType', header: 'Auction Type', width: 250 },
      { name: 'PodLevelSOVType', header: 'Pod Level SOV Type', width: 250 },
      // { name: 'IsPhiloFallbackAuction', header: 'Is Philo Fallback Auction', width: 250, }, // default set to false for now
      { name: 'NumberOfWinners', header: 'Number Of Winners', width: 250 },
      { name: 'PodComparePodDuration', header: 'Pod Duration', width: 200 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 200 },
      { name: 'HasAdvertiserRuleResponses', header: 'Has Advertiser Rule Responses', width: 200 },
    ],
    32: [
      // Advertiser Rule Report
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'PublisherID', header: 'Publisher ID', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentNetwork', header: 'Content Network', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'PodCompareType', header: 'Auction Type', width: 250 },
      { name: 'NumberOfWinners', header: 'Number Of Winners', width: 250 },
      { name: 'PodComparePodDuration', header: 'Pod Duration', width: 200 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 200 },
      { name: 'HasAdvertiserRuleResponses', header: 'Has Advertiser Rule Responses', width: 200 },
    ],
    33: [
      // Buyers APS Report
      { name: 'AppBundleID', header: 'App Bundle ID', width: 250 },
      { name: 'AppName', header: 'App Name', width: 250 },
      { name: 'APSKey', header: 'APS Key', width: 250 },
      { name: 'BidErrorCode', header: 'Error Type', width: 300 },
      { name: 'BidderGroup', header: 'Bidder Group', width: 250 },
      { name: 'BidderID', header: 'Bidder ID', width: 250 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'BidderType', header: 'Demand Source', width: 200 },
      { name: 'BidWonRejectReason', header: 'Bid Reject Reason', width: 300 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentNetwork', header: 'Content Network', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'DestinationID', header: 'Destination ID', width: 200 },
      { name: 'DestinationName', header: 'Destination Name', width: 200 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'DeviceSubType', header: 'Device Subtype', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'InventoryRouteID', header: 'Inventory Route ID', width: 200 },
      { name: 'InventoryRouteName', header: 'Inventory Route Name', width: 200 },
      { name: 'Livestream', header: 'Livestream', width: 200 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'SitePage', header: 'Site Page', width: 250 },
      { name: 'TargetingDevice', header: 'Device Environment', width: 200 },
    ],
    34: [
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'PublisherID', header: 'Publisher ID', width: 250 },
      { name: 'VastTrackerEndpointDomain', header: 'Third Party Tracker Domain', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'DealID', header: 'Deal ID', width: 250 },
      { name: 'BidderID', header: 'Bidder ID', width: 250 },
      { name: 'AppBundleID', header: 'App Bundle ID', width: 250 },
      { name: 'AppName', header: 'App Name', width: 250 },
      { name: 'BidderType', header: 'Demand Source', width: 200 },
      { name: 'ContentChannel', header: 'Content Channel', width: 250 },
      { name: 'ContentNetwork', header: 'Content Network', width: 250 },
    ],
    35: [
      // Elea AI Billing Report
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
    ],
    36: [
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'BidderID', header: 'Bidder ID', width: 250 },
      { name: 'RecognitionResultEnrichedLanguageInput', header: 'Creative', width: 250 },
    ],
    37: [
      // Bid Floor Settings Report has no real GroupBys.
      // These will be auto-populated in GroupByAsMap but not be displayed to the user.
      { name: 'BidderID', header: 'Bidder ID', width: 250 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
    ],
    // Publica Vault Report Group Bys
    38: [
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'IsBidSaver', header: 'Publica Vault', width: 200 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 150 },
      { name: 'SiteName', header: 'Channel Name', width: 350 },
    ],
    41: [
      { name: 'Country', header: 'Country', width: 200 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'InventoryRouteID', header: 'Inventory Route ID', width: 250 },
      { name: 'DestinationID', header: 'Destination ID', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'WeekOfYear', header: 'Week Of Year', width: 200 },
      { name: 'DestinationName', header: 'Destination Name', width: 250 },
      { name: 'InventoryRouteName', header: 'Inventory Route Name', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
    ],
    42: [
      { name: 'Country', header: 'Country', width: 200 },
      { name: 'CountryGroup', header: 'Region', width: 200 },
      { name: 'InventoryRouteID', header: 'Inventory Route ID', width: 250 },
      { name: 'DestinationID', header: 'Destination ID', width: 250 },
      { name: 'DeviceType', header: 'Device Type', width: 250 },
      { name: 'SiteID', header: 'Channel ID', width: 250 },
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'WeekOfYear', header: 'Week Of Year', width: 200 },
      { name: 'DestinationName', header: 'Destination Name', width: 250 },
      { name: 'InventoryRouteName', header: 'Inventory Route Name', width: 250 },
      { name: 'BidderID', header: 'Bidder ID', width: 250 },
      { name: 'BidderName', header: 'Bidder Name', width: 250 },
      { name: 'SiteName', header: 'Channel Name', width: 250 },
      { name: 'BidderType', header: 'Demand Source', width: 200 },
      { name: 'ChannelGroup', header: 'Channel Group', width: 250 },
    ],
    45: [
      // SSAI User Activity Report Groupings
      { name: 'Month', header: 'Month', width: 250 },
      { name: 'Day', header: 'Day', width: 250 },
      { name: 'Hour', header: 'Hour', width: 250 },
      { name: 'NetworkID', header: 'Network ID', width: 250 },
      { name: 'NetworkName', header: 'Network Name', width: 250 },
      { name: 'ContentID', header: 'Content ID', width: 250 },
      { name: 'StreamFormatType', header: 'Stream Format Type', width: 250 },
      { name: 'NumOfUsers', header: 'Number Of Users (Sessions Per User)', width: 250 },
    ],
  };

  if (publisherID === '381') {
    columnGroupingByReportID['3'].push({
      name: 'AdvertiserID',
      header: 'Advertiser ID',
      width: 250,
    });
    columnGroupingByReportID['3'].push({
      name: 'AdvertiserName',
      header: 'Advertiser Name',
      width: 250,
    });
  }
  if (publisherID === '408') {
    columnGroupingByReportID['22'].push({
      name: 'VenueID',
      header: 'Venue ID',
      width: 250,
    });
  }

  return columnGroupingByReportID;
};

function getColumns(defs: IColumn[], metrics?: string[]) {
  if (!metrics) {
    return defs;
  }
  const map = mapColumns(defs);

  if (metrics.includes('bidder_testing')) {
    metrics.push('ab_testing_config_name');
    metrics.push('ab_testing_test_name');
    delete metrics[metrics.indexOf('bidder_testing')];
  }
  return metrics
    .map((by: string) => {
      return map[colName(by)];
    })
    .filter((c?: IColumn) => {
      return !!c;
    });
}

export function getColumnIndex(name: string, cols: IColumn[]): number {
  for (let i = 0; i < cols.length; i += 1) {
    if (name === cols[i].name || name === keyToSQLField[cols[i].name]) {
      return i;
    }
  }
  return -1;
}

export function addGroupColumnsForSpecificPublishers(
  publisherID: string,
  reportType: string,
  columns: IColumn[],
  groupBy: string[],
): IColumn[] {
  // AB Testing columns
  if (groupBy.includes('bidder_testing')) {
    columns.push(abTestingConfigNameGroupByColumn);
    columns.push(abTestingTestNameGroupByColumn);
  }

  if (
    (publisherID === '391' || // Multicom
      publisherID === '530') && // Canela Media
    reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS
  ) {
    return [...columns, custom4GroupByColumn];
  }

  if (isSamsungPublisher(publisherID)) {
    // Samsung
    if (reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS) {
      return [
        ...columns,
        operatingSystemGroupByColumn,
        apkBuildGroupByColumn,
        samsungModelGroupByColumn,
        samsungGIVNParamExistsColumn,
      ];
    } else if (reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS) {
      return [
        ...columns,
        operatingSystemGroupByColumn,
        apkBuildGroupByColumn,
        samsungModelGroupByColumn,
        subtierGroupByColumn,
        samsungGIVNParamExistsColumn,
        buyerIDGroupByColumn,
      ];
    }
  }

  if (publisherID === '486' && reportType === REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT) {
    return [...columns, adomainGroupByColumn];
  }

  // VMAP share, fallback type and roku ad keys for Philo
  if (publisherID === '381') {
    if (reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS) {
      return [...columns, shareTypeGroupByColumn];
    } else if (reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS) {
      return [...columns, philoFallbackGroupByColumn, rokuAdKeysGroupByColumn];
    }
  }
  return columns;
}

export function getGroupColumnsByReportID(reportID: string, publisherID: string): IColumn[] {
  return getColumns(
    addGroupColumnsForSpecificPublishers(
      publisherID,
      reportID,
      getColumnGroupingByReportIDWithPublisherID(publisherID)[reportID] || [],
      [],
    ),
  );
}

const shareOfVoiceMetrics = [
  { name: 'BidAuctions', header: 'Auctions', width: 200 },
  { name: 'BidRequests', header: 'Bid Requests', width: 200 },
  { name: 'BidWons', header: 'Winning Bids', width: 200 },
  { name: 'Impressions', header: 'Impressions', width: 200 },
  { name: 'Revenue', header: 'Revenue', width: 200 },
];

const eleaAIAdQualityMetrics = [
  {
    name: 'EleaAINumTotalDetectedLanguagesBidErrors',
    header: 'Bid Errors (total detected languages, EleaAI)',
    width: 200,
  },
  {
    name: 'EleaAINumTotalDetectedLanguagesBidLosts',
    header: 'Lost Bids (total detected languages, EleaAI)',
    width: 200,
  },
  {
    name: 'EleaAINumTotalDetectedLanguagesBidResponses',
    header: 'Bid Responses (total detected languages, EleaAI)',
    width: 200,
  },
  {
    name: 'EleaAINumTotalDetectedLanguagesBidWons',
    header: 'Winning Bids (total detected languages, EleaAI)',
    width: 200,
  },
  {
    name: 'EleaAINumTotalDetectedLanguagesImpressions',
    header: 'Impressions (total detected languages, EleaAI)',
    width: 200,
  },
  { name: 'EleaAIDetectedLanguage', header: 'Detected Language', width: 200 },
  {
    name: 'EleaAIDetectedLanguageBlockedBids',
    header: 'Bids Blocked By Brand Safety Language Rule',
    width: 200,
  },
];

const auctionMetrics = [
  // Auction
  { name: 'BidResponseRate', header: 'Bid Response Rate', width: 200 },
  { name: 'BidTimeoutsRate', header: 'Bid Timeout Rate', width: 200 },
  { name: 'WinRate', header: 'Win Rate', width: 200 },
  { name: 'RenderRate', header: 'Render Rate', width: 200 },
  { name: 'FillRate', header: 'Fill Rate', width: 200 },
  { name: 'CompletionRate', header: 'Video Completion Rate', width: 200 },
  { name: 'RenderRateByPodDuration', header: 'Render Rate By Pod Duration', width: 200 },
  { name: 'PodClearRate', header: 'Pod Clear Rate', width: 200 },
  { name: 'PaidPodClearRate', header: 'Paid Pod Clear Rate', width: 200 },
  { name: 'RenderedPodFillRate', header: 'Rendered Pod Fill Rate', width: 200 },
  { name: 'PodFillRate', header: 'Pod Fill Rate', width: 200 },
  {
    name: 'EndpointRequests',
    header: getResellerTitle() !== 'Publica' ? 'Endpoint Requests' : 'Publica Endpoint Requests',
    width: 200,
  },
  {
    name: 'EndpointResponses',
    header: getResellerTitle() !== 'Publica' ? 'Endpoint Responses' : 'Publica Endpoint Responses',
    width: 200,
  },
  { name: 'BidAuctions', header: 'Auctions', width: 200 },
  { name: 'BidErrors', header: 'Bid Errors', width: 200 },
  { name: 'BidErrorRate', header: 'Bid Error Rate', width: 200 },
  { name: 'BidRequests', header: 'Bid Requests', width: 200 },
  { name: 'BidResponses', header: 'Bid Responses', width: 200 },
  { name: 'BidWons', header: 'Winning Bids', width: 200 },
  { name: 'AvgWinningBid', header: 'Average Winning Bid Price', width: 200 },
  { name: 'Impressions', header: 'Impressions', width: 200 },
  { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
  { name: 'AvgImpGrossECPM', header: 'Average Imp Gross eCPM', width: 200 },
  { name: 'NativeCompanionClick', header: 'Native Companion Click', width: 200 },
  { name: 'NativeCreativeView', header: 'Native Creative View', width: 200 },
  { name: 'NativeVideoClick', header: 'Native Video Click', width: 200 },
  { name: 'EndpointRequestECPM', header: 'Endpoint Request eCPM', width: 200 },
  { name: 'Revenue', header: 'Revenue', width: 200 },
  { name: 'GrossRevenue', header: 'Gross Revenue', width: 200 },
  { name: 'RenderedRequestedPodDuration', header: 'Rendered Requested Pod Duration', width: 200 },
  { name: 'RequestedPodDuration', header: 'Requested Pod Duration', width: 200 },
  { name: 'FilledPodDuration', header: 'Filled Pod Duration', width: 200 },
  { name: 'PaidFilledPodDuration', header: 'Paid Filled Pod Duration', width: 200 },
  { name: 'DeliveredPodDuration', header: 'Delivered Pod Duration', width: 200 },
  {
    name: 'TotalRenderedRequestedPodDuration',
    header: 'Total Rendered Requested Pod Duration',
    width: 200,
  },
  { name: 'TotalRequestedPodDuration', header: 'Total Requested Pod Duration', width: 200 },
  { name: 'TotalFilledPodDuration', header: 'Total Filled Pod Duration', width: 200 },
  { name: 'TotalPaidFilledPodDuration', header: 'Total Paid Filled Pod Duration', width: 200 },
  { name: 'TotalDeliveredPodDuration', header: 'Total Delivered Pod Duration', width: 200 },
  { name: 'EndpointLatency', header: 'Endpoint Latency', width: 150 },
  { name: 'AuctionLatency', header: 'Auction Latency', width: 150 },
  { name: 'EventEndpointBlocked', header: 'Event Endpoint Blocked', width: 150 },
  { name: 'EventEndpointErrors', header: 'Event Endpoint Errors', width: 150 },
  { name: 'AuctionClearRate', header: 'Auction Clear Rate', width: 200 },
  { name: 'Complete', header: 'Completes', width: 200 },
  { name: 'CPMPerSecond', header: 'CPM Per Second', width: 200 },
  { name: 'VPAIDErrors', header: 'VPAID Errors', width: 200 },
  { name: 'VPAIDStart', header: 'VPAID Started', width: 200 },
  { name: 'VPAIDImpressions', header: 'VPAID Impressions', width: 200 },
  { name: 'VPAIDQuartile1', header: 'VPAID 1st Quartiles', width: 200 },
  { name: 'VPAIDQuartile2', header: 'VPAID 2nd Quartiles', width: 200 },
  { name: 'VPAIDQuartile3', header: 'VPAID 3rd Quartiles', width: 200 },
  { name: 'VPAIDComplete', header: 'VPAID Completed', width: 200 },
  { name: 'BidSaverTrueLift', header: 'Publica Vault True Lift', width: 200 },
  { name: 'PartnerCPM', header: 'Partner CPM', width: 250 },
  { name: 'PartnerRevenue', header: 'Partner Revenue', width: 250 },
];

const bidderMetrics = [
  // Bidder
  { name: 'AvgBid', header: 'Average Bid Price', width: 200 },
  { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
  { name: 'AvgImpGrossECPM', header: 'Average Imp Gross eCPM', width: 200 },
  { name: 'AvgWinningBid', header: 'Average Winning Bid Price', width: 200 },
  { name: 'BidErrorRate', header: 'Bid Error Rate', width: 200 },
  { name: 'BidErrors', header: 'Bid Errors', width: 200 },
  { name: 'BidLossRatePrice', header: 'Loss Rate (Price)', width: 200 },
  { name: 'BidLossRateTier', header: 'Loss Rate (Tier)', width: 200 },
  { name: 'BidLosts', header: 'Lost Bids', width: 200 },
  { name: 'BidNone', header: 'No Bid', width: 200 },
  { name: 'BidRejections', header: 'Bid Rejections', width: 200 },
  { name: 'BidRequests', header: 'Bid Requests', width: 200 },
  { name: 'BidResponseRate', header: 'Bid Response Rate', width: 200 },
  { name: 'BidResponses', header: 'Bid Responses', width: 200 },
  { name: 'BidTimeouts', header: 'Bid Timeouts', width: 200 },
  { name: 'BidTimeoutsRate', header: 'Bid Timeout Rate', width: 200 },
  { name: 'BidWons', header: 'Winning Bids', width: 200 },
  { name: 'BuyersLatency', header: 'Bidder Latency', width: 150 },
  { name: 'Complete', header: 'Completes', width: 200 },
  { name: 'CompletionRate', header: 'Video Completion Rate', width: 200 },
  { name: 'CPMPerSecond', header: 'CPM Per Second', width: 200 },
  { name: 'GrossRevenue', header: 'Gross Revenue', width: 200 },
  { name: 'Impressions', header: 'Impressions', width: 200 },
  { name: 'NativeCompanionClick', header: 'Native Companion Click', width: 200 },
  { name: 'NativeCreativeView', header: 'Native Creative View', width: 200 },
  { name: 'NativeVideoClick', header: 'Native Video Click', width: 200 },
  { name: 'PartnerCPM', header: 'Partner CPM', width: 250 },
  { name: 'PartnerRevenue', header: 'Partner Revenue', width: 250 },
  { name: 'PostbidErrors', header: 'Postbid Errors', width: 200 },
  { name: 'PrebidErrors', header: 'Prebid Errors', width: 200 },
  { name: 'RenderRate', header: 'Render Rate', width: 200 },
  { name: 'Revenue', header: 'Revenue', width: 200 },
  { name: 'VastErrors', header: 'Vast Errors', width: 200 },
  { name: 'VPAIDCompleted', header: 'VPAID Completed', width: 200 },
  { name: 'VPAIDErrors', header: 'VPAID Errors', width: 200 },
  { name: 'VPAIDImpressions', header: 'VPAID Impressions', width: 200 },
  { name: 'VPAIDQuartile1', header: 'VPAID 1st Quartiles', width: 200 },
  { name: 'VPAIDQuartile2', header: 'VPAID 2nd Quartiles', width: 200 },
  { name: 'VPAIDQuartile3', header: 'VPAID 3rd Quartiles', width: 200 },
  { name: 'VPAIDStarted', header: 'VPAID Starts', width: 200 },
  { name: 'WinRate', header: 'Win Rate', width: 200 },
  { name: 'ClearRateByPodDuration', header: 'Clear Rate by Pod Duration', width: 250 },
  { name: 'TotalFilledPodDuration', header: 'Total Filled Pod Duration', width: 250 },
  {
    name: 'MaxAvailablePodDurationByTier',
    header: 'Max Available Pod Duration by Tier',
    width: 250,
  },
];

const apsBuyersMetric = [
  { name: 'AvgBid', header: 'Average Bid Price', width: 200 },
  { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
  { name: 'AvgImpGrossECPM', header: 'Average Imp Gross eCPM', width: 200 },
  { name: 'AvgWinningBid', header: 'Average Winning Bid Price', width: 200 },
  { name: 'BidErrorRate', header: 'Bid Error Rate', width: 200 },
  { name: 'BidErrors', header: 'Bid Errors', width: 200 },
  { name: 'BidLossRatePrice', header: 'Loss Rate (Price)', width: 200 },
  { name: 'BidLossRateTier', header: 'Loss Rate (Tier)', width: 200 },
  { name: 'BidLosts', header: 'Lost Bids', width: 200 },
  { name: 'BidNone', header: 'No Bid', width: 200 },
  { name: 'BidRejections', header: 'Bid Rejections', width: 200 },
  { name: 'BidRequests', header: 'Bid Requests', width: 200 },
  { name: 'BidResponseRate', header: 'Bid Response Rate', width: 200 },
  { name: 'BidResponses', header: 'Bid Responses', width: 200 },
  { name: 'BidTimeouts', header: 'Bid Timeouts', width: 200 },
  { name: 'BidTimeoutsRate', header: 'Bid Timeout Rate', width: 200 },
  { name: 'BidWons', header: 'Winning Bids', width: 200 },
  { name: 'BuyersLatency', header: 'Bidder Latency', width: 150 },
  { name: 'Complete', header: 'Completes', width: 200 },
  { name: 'CompletionRate', header: 'Video Completion Rate', width: 200 },
  { name: 'CPMPerSecond', header: 'CPM Per Second', width: 200 },
  { name: 'GrossRevenue', header: 'Gross Revenue', width: 200 },
  { name: 'Impressions', header: 'Impressions', width: 200 },
  { name: 'PostbidErrors', header: 'Postbid Errors', width: 200 },
  { name: 'PrebidErrors', header: 'Prebid Errors', width: 200 },
  { name: 'RenderRate', header: 'Render Rate', width: 200 },
  { name: 'Revenue', header: 'Revenue', width: 200 },
  { name: 'VastErrors', header: 'Vast Errors', width: 200 },
  { name: 'VPAIDCompleted', header: 'VPAID Completed', width: 200 },
  { name: 'VPAIDErrors', header: 'VPAID Errors', width: 200 },
  { name: 'VPAIDImpressions', header: 'VPAID Impressions', width: 200 },
  { name: 'VPAIDQuartile1', header: 'VPAID 1st Quartiles', width: 200 },
  { name: 'VPAIDQuartile2', header: 'VPAID 2nd Quartiles', width: 200 },
  { name: 'VPAIDQuartile3', header: 'VPAID 3rd Quartiles', width: 200 },
  { name: 'VPAIDStarted', header: 'VPAID Starts', width: 200 },
  { name: 'WinRate', header: 'Win Rate', width: 200 },
];

const columnMetricsByReportType: { [key: string]: IColumn[] } = {
  // to replace
  2: auctionMetrics,
  3: bidderMetrics,
  4: [
    // SSP
    { name: 'BidResponseRate', header: 'Bid Response Rate', width: 200 },
    { name: 'WinRate', header: 'Win Rate', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'BidRequests', header: 'Bid Requests', width: 200 },
    { name: 'BidResponses', header: 'Bid Responses', width: 200 },
    { name: 'BidTimeouts', header: 'Bid Timeouts', width: 200 },
    { name: 'BidErrors', header: 'Bid Errors', width: 200 },
    { name: 'BidErrorRate', header: 'Bid Error Rate', width: 200 },
    { name: 'BidWons', header: 'Winning Bids', width: 200 },
    { name: 'BidLosts', header: 'Lost Bids', width: 200 },
    { name: 'AvgBid', header: 'Average Bid Price', width: 200 },
    { name: 'AvgWinningBid', header: 'Average Winning Bid Price', width: 200 },
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
    { name: 'AvgImpGrossECPM', header: 'Average Imp Gross eCPM', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
    { name: 'GrossRevenue', header: 'Gross Revenue', width: 200 },
  ],
  5: [
    // Campaign Report
    { name: 'FillRate', header: 'Line Item Fill Rate', width: 200 },
    { name: 'CompletionRate', header: 'Video Completion Rate', width: 200 },
    { name: 'CampaignRequest', header: 'Line Item Paced Request', width: 200 },
    { name: 'CampaignInventoryRequest', header: 'Line Item Raw Request', width: 200 },
    { name: 'CampaignFilled', header: 'Line Item Filled', width: 200 },
    { name: 'CampaignImpression', header: 'Line Item Impressions', width: 200 },
    { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
    { name: 'CampaignStart', header: 'Line Item Starts', width: 200 },
    { name: 'CampaignQuartile1', header: 'Line Item Quartile1', width: 200 },
    { name: 'CampaignQuartile2', header: 'Line Item Quartile2', width: 200 },
    { name: 'CampaignQuartile3', header: 'Line Item Quartile3', width: 200 },
    { name: 'CampaignComplete', header: 'Line Item Complete', width: 200 },
    { name: 'CampaignNoFill', header: 'Line Item No Fill', width: 200 },
    { name: 'LineItemCount', header: 'Line Item Count', width: 200 },
  ],
  6: [
    // Audience Segments
    { name: 'BidResponses', header: 'Bid Responses', width: 200 },
    { name: 'BidWons', header: 'Winning Bids', width: 200 },
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
  ],
  7: [
    // SSAI Report Metrics
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'AdComplete', header: 'Ad Complete', width: 200 },
    { name: 'AdFilled', header: 'Ad Filled', width: 200 },
    { name: 'AdImpression', header: 'Ad Impression', width: 200 },
    { name: 'AdFirstQuartile', header: 'Ad First Quartile', width: 200 },
    { name: 'AdMidPoint', header: 'Ad Mid Point', width: 200 },
    { name: 'AdThirdQuartile', header: 'Ad Third Quartile', width: 200 },
    { name: 'AvgStreamAdFilledLatency', header: 'Avg Stream Ad Filled Latency', width: 200 },
    {
      name: 'AvgStreamChunklistResponseLatency',
      header: 'Avg Stream Chunklist Response Latency',
      width: 200,
    },
    { name: 'AvgStreamResponseLatency', header: 'Avg Stream Response Latency', width: 200 },
    { name: 'HLSReject', header: 'HLS Reject', width: 200 },
    { name: 'DurationExceeded', header: 'Duration Exceeded', width: 200 },
    { name: 'PodStart', header: 'Pod Start Non-CST', width: 200 },
    { name: 'PodComplete', header: 'Pod Complete', width: 200 },
    { name: 'PodCompleteRate', header: 'Pod Complete Rate', width: 200 },
    { name: 'PodFilled', header: 'Pod Filled', width: 200 },
    { name: 'PodRequest', header: 'Pod Requests', width: 200 },
    { name: 'StreamAdsFetchTimeout', header: 'Stream Ads Fetch Timeout', width: 200 },
    { name: 'StreamChunklistTimeout', header: 'Stream Chunklist Timeout', width: 200 },
    { name: 'StreamError', header: 'Stream Error', width: 200 },
    { name: 'StreamPlaylistTimeout', header: 'Stream Playlist Timeout', width: 200 },
    { name: 'StreamStart', header: 'Stream Start', width: 200 },
    { name: 'AvgStreamStartPerSession', header: 'Avg Stream Start per Session', width: 200 },
    { name: 'AvgPodRequestPerSession', header: 'Avg Pod Request per Session', width: 200 },
    { name: 'AvgPodFilledPerSession', header: 'Avg Pod Filled per Session', width: 200 },
    { name: 'AvgPodCompletePerSession', header: 'Avg Pod Complete per Session', width: 200 },
    { name: 'AvgAdFilledPerSession', header: 'Avg Ad Filled per Session', width: 200 },
    { name: 'AvgAdImpressionPerSession', header: 'Avg Ad Impression per Session', width: 200 },
    { name: 'AvgAdCompletePerSession', header: 'Avg Ad Complete per Session', width: 200 },
    { name: 'AvgStreamErrorPerSession', header: 'Avg Stream Error per Session', width: 200 },
    // cst (client-side tracking)
    { name: 'CstRenderRate', header: 'Render Rate CST', width: 200 },
    { name: 'CstAdFilled', header: 'Ad Filled CST', width: 200 },
    { name: 'CstAdImpression', header: 'Ad Impression CST', width: 200 },
    { name: 'NonCstRenderRate', header: 'Render Rate Non-CST', width: 200 },
    { name: 'NonCstAdFilled', header: 'Ad Filled Non-CST', width: 200 },
    { name: 'NonCstAdImpression', header: 'Ad Impression Non-CST', width: 200 },
    { name: 'AdImpressionSS', header: 'Target Ad Impression for CST', width: 200 },
    { name: 'PodStartSS', header: 'Pod Start for CST', width: 200 },
  ],
  8: [
    // Unique User Report: Unique Users
    { name: 'UniqueUsers', header: 'Unique Users', width: 200 },
  ],
  9: [
    // Audience For All Publisher
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'PublisherCPM', header: 'Publisher eCPM', width: 200 },
    { name: 'Revenue', header: 'Publisher Revenue', width: 200 },
    { name: 'LiverampCPM', header: 'Liveramp eCPM', width: 200 },
    { name: 'LiverampRevenue', header: 'Liveramp Revenue', width: 200 },
    { name: 'PublicaCPM', header: 'Publica eCPM', width: 200 },
    { name: 'PublicaRevenue', header: 'Publica Revenue', width: 200 },
    { name: 'CostToPublisher', header: 'Cost To Publisher', width: 200 },
    { name: 'PublisherMargin', header: 'Publisher Margin', width: 200 },
  ],
  10: [
    // Auction Fill Rate
    { name: 'BidRequestRate', header: 'Bid Requested Rate', width: 200 },
    { name: 'FillRateByDemandSource', header: 'Fill Rate', width: 200 },
    { name: 'BidAuctions', header: 'Auctions', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
  ],
  13: [
    // Brand Safety Tracker
    { name: 'Impressions', header: 'Impressions', width: 200 },
    {
      name: 'EndpointResponses',
      header:
        getResellerTitle() !== 'Publica' ? 'Endpoint Responses' : 'Publica Endpoint Responses',
      width: 200,
    },
    { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
    { name: 'AvgImpGrossECPM', header: 'Average Imp Gross eCPM', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
    { name: 'GrossRevenue', header: 'Gross Revenue', width: 200 },
    { name: 'EventStart', header: 'Start', width: 200 },
    { name: 'Quartile1', header: 'Quartile1', width: 200 },
    { name: 'Quartile2', header: 'Quartile2', width: 200 },
    { name: 'Quartile3', header: 'Quartile3', width: 200 },
    { name: 'Complete', header: 'Complete', width: 200 },
  ],
  14: [
    // Deals
    { name: 'BidResponseRate', header: 'Bid Response Rate', width: 200 },
    { name: 'WinRate', header: 'Win Rate', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'FwWinRate', header: 'ORTB Win Rate', width: 200 },
    { name: 'FwRenderRate', header: 'ORTB Render Rate', width: 200 },
    { name: 'CompletionRate', header: 'Video Completion Rate', width: 200 },
    { name: 'BidRequests', header: 'Bid Requests', width: 200 },
    { name: 'BidResponses', header: 'Bid Responses', width: 200 },
    { name: 'BidTimeouts', header: 'Bid Timeouts', width: 200 },
    { name: 'BidErrors', header: 'Bid Errors', width: 200 },
    { name: 'BidErrorRate', header: 'Bid Error Rate', width: 200 },
    { name: 'BidWons', header: 'Winning Bids', width: 200 },
    { name: 'Impressions', header: 'Impressions', width: 200 },
    {
      name: 'EndpointRequests',
      header: getResellerTitle() !== 'Publica' ? 'Endpoint Requests' : 'Publica Endpoint Requests',
      width: 200,
    },
    {
      name: 'EndpointResponses',
      header:
        getResellerTitle() !== 'Publica' ? 'Endpoint Responses' : 'Publica Endpoint Responses',
      width: 200,
    },
    { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
    { name: 'AvgImpGrossECPM', header: 'Average Imp Gross eCPM', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
    { name: 'GrossRevenue', header: 'Gross Revenue', width: 200 },
    { name: 'BidNone', header: 'No Bid', width: 200 },
  ],
  15: [
    // Publica Deals
    { name: 'BidResponseRate', header: 'Bid Response Rate', width: 200 },
    { name: 'WinRate', header: 'Win Rate', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'CompletionRate', header: 'Video Completion Rate', width: 200 },
    { name: 'BidRequests', header: 'Bid Requests', width: 200 },
    { name: 'BidResponses', header: 'Bid Responses', width: 200 },
    { name: 'BidTimeouts', header: 'Bid Timeouts', width: 200 },
    { name: 'BidErrors', header: 'Bid Errors', width: 200 },
    { name: 'BidErrorRate', header: 'Bid Error Rate', width: 200 },
    { name: 'BidWons', header: 'Winning Bids', width: 200 },
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
    { name: 'AvgImpGrossECPM', header: 'Average Imp Gross eCPM', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
    { name: 'GrossRevenue', header: 'Gross Revenue', width: 200 },
    { name: 'BidNone', header: 'No Bid', width: 200 },
  ],
  17: [
    // Inventory Routes
    { name: 'DestinationImpressions', header: 'Destination Imps', width: 200 },
    { name: 'InventoryRouteRequests', header: 'Inventory Route Requests', width: 200 },
    { name: 'InventoryRouteResponses', header: 'Inventory Route Responses', width: 200 },
    { name: 'InventoryRouteErrors', header: 'Inventory Route Errors', width: 200 },
    { name: 'InventoryRouteEndpointLatency', header: 'Inventory Route Latency', width: 200 },
    { name: 'InventoryRouteNoFills', header: 'Inventory Route No Fills', width: 200 },
  ],
  18: [
    // Destinations
    { name: 'DestinationImpressions', header: 'Destination Imps', width: 200 },
    { name: 'DestinationRequests', header: 'Destination Requests', width: 200 },
    { name: 'DestinationResponses', header: 'Destination Responses', width: 200 },
    { name: 'DestinationErrors', header: 'Destination Errors', width: 200 },
    { name: 'DestinationTimeouts', header: 'Destination Timeouts', width: 200 },
    { name: 'DestinationRequestLatency', header: 'Destination Request Latency', width: 200 },
    { name: 'DestinationNoFills', header: 'Destination No Fills', width: 200 },
  ],

  19: [
    // Contextual Segments
    {
      name: 'EndpointRequests',
      header: getResellerTitle() !== 'Publica' ? 'Endpoint Requests' : 'Publica Endpoint Requests',
      width: 200,
    },
    { name: 'Complete', header: 'Completes', width: 200 },
    { name: 'BidRequests', header: 'Bid Requests', width: 200 },
    { name: 'BidResponses', header: 'Bid Responses', width: 200 },
    { name: 'BidWons', header: 'Winning Bids', width: 200 },
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'AvgImpECPM', header: 'Average Imp eCPM', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
  ],
  20: [
    // IAS Third Party
    { name: 'TrackedAds', header: 'Tracked Ads', width: 200 },
    { name: 'ImpressionRendered', header: 'Impression Rendered', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'IVTAds', header: 'IVT Ads', width: 200 },
    { name: 'IVTRate', header: 'Invalid Traffic Rate', width: 200 },
    { name: 'EligibleAds', header: 'Eligible Ads', width: 200 },
    { name: 'MeasuredAds', header: 'Measured Ads', width: 200 },
    { name: 'MeasuredRate', header: 'Measured Rate', width: 200 },
    { name: 'ViewableImpression', header: 'Viewable Impression', width: 200 },
    { name: 'ViewabilityRate', header: 'Viewability Rate', width: 200 },
    { name: 'AdBlockCount', header: 'Ad Block Count', width: 200 },
    { name: 'AdBlockRate', header: 'Ad Block Rate', width: 200 },
  ],
  21: [
    // IAS First Party
    { name: 'TrackedAds', header: 'Tracked Ads', width: 200 },
    { name: 'ImpressionRendered', header: 'Impression Rendered', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'IVTAds', header: 'IVT Ads', width: 200 },
    { name: 'IVTRate', header: 'Invalid Traffic Rate', width: 200 },
    { name: 'MeasuredAds', header: 'Measured Ads', width: 200 },
    { name: 'MeasuredRate', header: 'Measured Rate', width: 200 },
    { name: 'ViewableImpression', header: 'Viewable Impression', width: 200 },
    { name: 'ViewabilityRate', header: 'Viewability Rate', width: 200 },
    { name: 'EligibleAds', header: 'Eligible Ads', width: 200 },
    { name: 'AdBlockCount', header: 'Ad Block Count', width: 200 },
    { name: 'AdBlockRate', header: 'Ad Block Rate', width: 200 },
  ],
  22: [
    // Creative Validation
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'BidWons', header: 'Winning Bids', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
  ],
  23: [
    // EleaAI Scan All
    { name: 'EleaAIBidResponses', header: 'Bid Responses (with adomains, EleaAI)', width: 200 },
    { name: 'EleaAIBidWons', header: 'Winning Bids (with adomains, EleaAI)', width: 200 },
    { name: 'EleaAIBidLosts', header: 'Lost Bids (with adomains, EleaAI)', width: 200 },
    { name: 'EleaAIBidErrors', header: 'Bid Errors (with adomains, EleaAI)', width: 200 },
    { name: 'EleaAIImpressions', header: 'Impressions (with adomains, EleaAI)', width: 200 },
    // ---
    {
      name: 'EleaAIBidResponsesDifferentAdomain',
      header: 'Bid Responses with different Adomains (different adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAIBidWonsDifferentAdomain',
      header: 'Winning Bids with different Adomains (different adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAIBidLostsDifferentAdomain',
      header: 'Lost Bids with different Adomains (different adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAIBidErrorsDifferentAdomain',
      header: 'Bid Errors with different Adomains (different adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAIImpressionsDifferentAdomain',
      header: 'Impressions with different Adomains (different adomains, EleaAI)',
      width: 200,
    },
    // ---
    {
      name: 'EleaAIBidResponsesDifferentAdomainRate',
      header: 'Bid Responses with different Adomains Rate',
      width: 200,
    },
    {
      name: 'EleaAIBidWonsDifferentAdomainRate',
      header: 'Winning Bids with different Adomains Rate',
      width: 200,
    },
    {
      name: 'EleaAIBidLostsDifferentAdomainRate',
      header: 'Lost Bids with different Adomains Rate',
      width: 200,
    },
    {
      name: 'EleaAIBidErrorsDifferentAdomainRate',
      header: 'Bid Errors with different Adomains Rate',
      width: 200,
    },
    {
      name: 'EleaAIImpressionsDifferentAdomainRate',
      header: 'Impressions with different Adomains Rate',
      width: 200,
    },
    // ---
    {
      name: 'EleaAINumTotalDetectedLanguagesBidErrors',
      header: 'Bid Errors (total detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalDetectedLanguagesBidLosts',
      header: 'Lost Bids (total detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalDetectedLanguagesBidResponses',
      header: 'Bid Responses (total detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalDetectedLanguagesBidWons',
      header: 'Winning Bids (total detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalDetectedLanguagesImpressions',
      header: 'Impressions (total detected languages, EleaAI)',
      width: 200,
    },
    // ---
    {
      name: 'EleaAINumUniqueDetectedLanguagesBidErrors',
      header: 'Bid Errors (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueDetectedLanguagesBidLosts',
      header: 'Lost Bids (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueDetectedLanguagesBidResponses',
      header: 'Bid Responses (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueDetectedLanguagesBidWons',
      header: 'Winning Bids (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueDetectedLanguagesImpressions',
      header: 'Impressions (unique detected languages, EleaAI)',
      width: 200,
    },
    // ---
    { name: 'EleaAIEnrichedAdomain', header: 'Adomain from Elea AI', width: 200 },
    { name: 'EleaAIOriginalAdomain', header: 'Adomain from Bid', width: 200 },
    { name: 'EleaAIMediafile', header: 'Mediafile from the Bid', width: 200 },
    { name: 'EleaAIDetectedLanguage', header: 'Detected Language', width: 200 },
    {
      name: 'EleaAIDetectedLanguageBlockedBids',
      header: 'Bids Blocked By Brand Safety Language Rule',
      width: 200,
    },
  ],
  24: [
    ...eleaAIAdQualityMetrics,
    {
      name: 'EleaAINumUniqueDetectedLanguagesBidErrors',
      header: 'Bid Errors (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueDetectedLanguagesBidLosts',
      header: 'Lost Bids (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueDetectedLanguagesBidResponses',
      header: 'Bid Responses (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueDetectedLanguagesBidWons',
      header: 'Winning Bids (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueDetectedLanguagesImpressions',
      header: 'Impressions (unique detected languages, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalMediafilesBidResponses',
      header: 'Bid Responses (total mediafiles, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalMediafilesBidWons',
      header: 'Winning Bids (total mediafiles, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalMediafilesBidLosts',
      header: 'Lost Bids (total mediafiles, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalMediafilesBidErrors',
      header: 'Bid Errors (total mediafiles, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalMediafilesImpressions',
      header: 'Impressions (total mediafiles, EleaAI)',
      width: 200,
    },
    // ---
    {
      name: 'EleaAINumUniqueMediafilesBidResponses',
      header: 'Bid Responses (unique mediafiles, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueMediafilesBidWons',
      header: 'Winning Bids (unique mediafiles, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueMediafilesBidLosts',
      header: 'Lost Bids (unique mediafiles, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueMediafilesBidErrors',
      header: 'Bid Errors (unique mediafiles, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueMediafilesImpressions',
      header: 'Impressions (unique mediafiles, EleaAI)',
      width: 200,
    },
    // ---
    {
      name: 'EleaAINumTotalAdomainsBidResponses',
      header: 'Bid Responses (total adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalAdomainsBidWons',
      header: 'Winning Bids (total adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalAdomainsBidLosts',
      header: 'Lost Bids (total adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalAdomainsBidErrors',
      header: 'Bid Errors (total adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumTotalAdomainsImpressions',
      header: 'Impressions (total adomains, EleaAI)',
      width: 200,
    },
    // ---
    {
      name: 'EleaAINumUniqueAdomainsBidResponses',
      header: 'Bid Responses (unique adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueAdomainsBidWons',
      header: 'Winning Bids (unique adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueAdomainsBidLosts',
      header: 'Lost Bids (unique adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueAdomainsBidErrors',
      header: 'Bid Errors (unique adomains, EleaAI)',
      width: 200,
    },
    {
      name: 'EleaAINumUniqueAdomainsImpressions',
      header: 'Impressions (unique adomains, EleaAI)',
      width: 200,
    },
    // ---
    {
      name: 'EleaAIBidWonRate',
      header: 'Winning Bid Rate by EleaAI (wons/resps, adomains)',
      width: 200,
    },
    {
      name: 'EleaAIImpressionRate',
      header: 'Impression Rate by EleaAI (imps/wons, adomains)',
      width: 200,
    },
  ],
  25: [
    // IAS Performance Report
    // Summary
    { name: 'TotalImps', header: 'Total Tracked Ads', width: 200 },
    { name: 'ImpressionRendered', header: 'Rendered Impressions', width: 200 },
    { name: 'IVTAds', header: 'Invalid Traffic Ads', width: 200 },
    { name: 'EligibleAds', header: 'Eligible Ads', width: 200 },
    { name: 'MeasuredAds', header: 'Measured Ads', width: 200 },
    { name: 'ViewableImpression', header: 'Viewable Impressions', width: 200 },
    { name: 'AdBlockCount', header: 'Blocked Ads', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'IVTRate', header: 'Invalid Traffic Rate', width: 200 },
    { name: 'MeasuredRate', header: 'Measured Rate', width: 200 },
    { name: 'ViewabilityRate', header: 'Viewability Rate', width: 200 },
    { name: 'AdBlockRate', header: 'Blocked Rate', width: 200 },
    // GroupM Viewability
    { name: 'GroupmFullyInViewDisplayImps', header: 'GroupM Display Impressions', width: 200 },
    { name: 'GroupmFullyInViewVideoImps', header: 'GroupM Video Impressions', width: 200 },
    { name: 'TotalBillableImps', header: 'Total Billable Impressions', width: 200 },
    { name: 'GroupmDisplayViewableRate', header: 'GroupM Display Viewable Rate', width: 200 },
    { name: 'GroupmVideoViewableRate', header: 'GroupM Video Viewable Rate', width: 200 },
    // Invalid Traffic
    { name: 'SittingDuckBotImps', header: 'Sitting Duck Bot Impressions', width: 200 },
    { name: 'StandardBotImps', header: 'Standard Bot Impressions', width: 200 },
    { name: 'VolunteerBotImps', header: 'Volunteer Bot Impressions', width: 200 },
    { name: 'ProfileBotImps', header: 'Profile Bot Impressions', width: 200 },
    { name: 'MaskedBotImps', header: 'Masked Bot Impressions', width: 200 },
    { name: 'NomadicBotImps', header: 'Nomadic Bot Impressions', width: 200 },
    { name: 'OtherBotImps', header: 'Other Bot Impressions', width: 200 },
    // Failure Risk Categories
    { name: 'FailedAdultAds', header: 'Failed Adult Ads', width: 200 },
    { name: 'FailedAlcoholAds', header: 'Failed Alcohol Ads', width: 200 },
    { name: 'FailedHateSpeechAds', header: 'Failed Hate Speech Ads', width: 200 },
    { name: 'FailedIllegalDownloadsAds', header: 'Failed Illegal Downloads Ads', width: 200 },
    { name: 'FailedDrugsAds', header: 'Failed Drugs Ads', width: 200 },
    { name: 'FailedOffensiveLanguageAds', header: 'Failed Offensive Language Ads', width: 200 },
    { name: 'FailedViolenceAds', header: 'Failed Violence Ads', width: 200 },
    // Ad Refresh
    { name: 'RefreshImps', header: 'Refresh Ads', width: 200 },
    { name: 'RefreshThresholdEvents', header: 'IAS Refreshed Ads', width: 200 },
    { name: 'RefreshedAdRate', header: 'Refreshed Ad Rate', width: 200 },
    { name: 'IASRefreshedAdRate', header: 'IAS Refreshed Ad Rate', width: 200 },
    // Publicis Viewability
    {
      name: 'PublicisFullyInViewDisplayImps',
      header: 'Publicis Fully In View Display Impressions',
      width: 200,
    },
    {
      name: 'PublicisFullyInViewVideoImps',
      header: 'Publicis Fully In View Video Impressions',
      width: 200,
    },
    { name: 'PublicisDisplayViewableRate', header: 'Publicis Display Viewable Rate', width: 200 },
    { name: 'PublicisVideoViewableRate', header: 'Publicis Video Viewable Rate', width: 200 },
    // Blocked Reasons
    { name: 'BlockedContentImps', header: 'Blocked by Brand Safety', width: 200 },
    { name: 'BlockedSuitabilityImps', header: 'Blocked by Brand Suitability', width: 200 },
    { name: 'BlockedKeywordImps', header: 'Blocked by Custom Keyword Exclusion List', width: 200 },
    {
      name: 'BlockedCustomListImps',
      header: 'Blocked by Custom URL/App Exclusion List',
      width: 200,
    },
    { name: 'BlockedGeoImps', header: 'Blocked by Geo', width: 200 },
    { name: 'BlockedSuspiciousImps', header: 'Blocked by Invalid Traffic', width: 200 },
    { name: 'BlockedSeeThroughImps', header: 'Blocked by Invisible URL', width: 200 },
    { name: 'BlockedLanguageImps', header: 'Blocked by Target Language Mismatch', width: 200 },
    // Exposure
    { name: 'InViewAtPageOpenRate', header: 'In View At Page Open Rate', width: 200 },
    { name: 'InViewOver5sRate', header: 'In View > 5s Rate', width: 200 },
    { name: 'InViewOver15sRate', header: 'In View > 15s Rate', width: 200 },
    // Out Of View
    { name: 'MrcOvrNetImps', header: 'Not Enough Time', width: 200 },
    { name: 'MrcOvrNepImps', header: 'Not Enough Pixels', width: 200 },
    { name: 'MrcOvrNivImps', header: 'Never On Screen', width: 200 },
    { name: 'MrcOvrOofImps', header: 'Out Of Focus', width: 200 },
    { name: 'MrcOvrNsImps', header: 'Video Never Started', width: 200 },
    { name: 'MrcOvrUnkImps', header: 'Other Out Of View', width: 200 },
    // Failure Reasons
    { name: 'FailedContentImps', header: 'Failed for Brand Safety', width: 200 },
    { name: 'FailedSuitabilityImps', header: 'Failed for Brand Suitability', width: 200 },
    { name: 'FailedKeywordImps', header: 'Failed for Custom Keyword Exclusion List', width: 200 },
    {
      name: 'FailedCustomListImps',
      header: 'Failed for Custom URL/App Exclusion List',
      width: 200,
    },
    { name: 'FailedGeoImps', header: 'Failed for Geo', width: 200 },
    { name: 'FailedSuspiciousImps', header: 'Failed for Invalid Traffic', width: 200 },
    { name: 'FailedSeeThroughImps', header: 'Failed for Invisible URL', width: 200 },
    { name: 'FailedLanguageImps', header: 'Failed for Target Language Mismatch', width: 200 },
    // Blocked Risk Categories
    { name: 'BlockedContentAdultImps', header: 'Blocked Adult Ads', width: 200 },
    { name: 'BlockedContentAlcoholImps', header: 'Blocked Alcohol Ads', width: 200 },
    { name: 'BlockedContentHateImps', header: 'Blocked Hate Speech Ads', width: 200 },
    { name: 'BlockedContentDownloadsImps', header: 'Blocked Illegal Downloads Ads', width: 200 },
    { name: 'BlockedContentDrugsImps', header: 'Blocked Drugs Ads', width: 200 },
    { name: 'BlockedContentOffensiveImps', header: 'Blocked Offensive Language Ads', width: 200 },
    { name: 'BlockedContentViolenceImps', header: 'Blocked Violence Ads', width: 200 },
    // Video
    { name: 'VideoInViewRate', header: '% In View', width: 200 },
    { name: 'VideoOutOfViewRate', header: '% Out Of View', width: 200 },
    { name: 'VideoNeverStartedRate', header: '% Ad Never Started', width: 200 },
    { name: 'VideoCompletedRate', header: '% Completed', width: 200 },
    { name: 'VideoCompletedInViewRate', header: '% Completed In View', width: 200 },
    { name: 'VideoMutedRate', header: '% Muted', width: 200 },
    { name: 'VideoFullScreenRate', header: '% Full Screen', width: 200 },
    { name: 'VideoClickThroughRate', header: '% Click Through (CTR)', width: 200 },
    // Invalid Traffic by Type
    { name: 'FraudGivtImps', header: 'General Invalid Traffic (GIVT) Ads', width: 200 },
    { name: 'FraudNhtImps', header: 'Bots (SIVT) Ads', width: 200 },
    { name: 'FraudHaImps', header: 'Hidden Ads (SIVT)', width: 200 },
    { name: 'FraudDsImps', header: 'Domain Spoofing (SIVT)', width: 200 },
    { name: 'FraudIbImps', header: 'Incentivized Browsing (RVI) Ads', width: 200 },
    { name: 'FraudLspImps', header: 'Proxy Servers (RVI) Ads', width: 200 },
    {
      name: 'SophisticatedInvalidTrafficAds',
      header: 'Sophisticated Invalid Traffic Ads',
      width: 200,
    },
    { name: 'ReducedValueInventoryAds', header: 'Reduced Value Inventory Ads', width: 200 },
    { name: 'GeneralInvalidTrafficRate', header: 'General Invalid Traffic Rate', width: 200 },
    {
      name: 'SophisticatedInvalidTrafficRate',
      header: 'Sophisticated Invalid Traffic Rate',
      width: 200,
    },
    { name: 'ReducedValueInventoryRate', header: 'Reduced Value Inventory Rate', width: 200 },
  ],
  26: [
    // IAS Device Report
    // Summary
    { name: 'TotalImps', header: 'Total Tracked Ads', width: 200 },
    { name: 'ImpressionRendered', header: 'Rendered Impressions', width: 200 },
    { name: 'IVTAds', header: 'Invalid Traffic Ads', width: 200 },
    { name: 'MeasuredAds', header: 'Measured Ads', width: 200 },
    { name: 'ViewableImpression', header: 'Viewable Impressions', width: 200 },
    { name: 'AdBlockCount', header: 'Blocked Ads', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'IVTRate', header: 'Invalid Traffic Rate', width: 200 },
    { name: 'MeasuredRate', header: 'Measured Rate', width: 200 },
    { name: 'ViewabilityRate', header: 'Viewability Rate', width: 200 },
    { name: 'AdBlockRate', header: 'Blocked Rate', width: 200 },
    // GroupM Viewability
    { name: 'GroupmFullyInViewDisplayImps', header: 'GroupM Display Impressions', width: 200 },
    { name: 'GroupmFullyInViewVideoImps', header: 'GroupM Video Impressions', width: 200 },
    { name: 'TotalBillableImps', header: 'Total Billable Impressions', width: 200 },
    { name: 'GroupmDisplayViewableRate', header: 'GroupM Display Viewable Rate', width: 200 },
    { name: 'GroupmVideoViewableRate', header: 'GroupM Video Viewable Rate', width: 200 },
    // Invalid Traffic
    { name: 'SittingDuckBotImps', header: 'Sitting Duck Bot Impressions', width: 200 },
    { name: 'StandardBotImps', header: 'Standard Bot Impressions', width: 200 },
    { name: 'VolunteerBotImps', header: 'Volunteer Bot Impressions', width: 200 },
    { name: 'ProfileBotImps', header: 'Profile Bot Impressions', width: 200 },
    { name: 'MaskedBotImps', header: 'Masked Bot Impressions', width: 200 },
    { name: 'NomadicBotImps', header: 'Nomadic Bot Impressions', width: 200 },
    { name: 'OtherBotImps', header: 'Other Bot Impressions', width: 200 },
    // Failure Risk Categories
    { name: 'FailedAdultAds', header: 'Failed Adult Ads', width: 200 },
    { name: 'FailedAlcoholAds', header: 'Failed Alcohol Ads', width: 200 },
    { name: 'FailedHateSpeechAds', header: 'Failed Hate Speech Ads', width: 200 },
    { name: 'FailedIllegalDownloadsAds', header: 'Failed Illegal Downloads Ads', width: 200 },
    { name: 'FailedDrugsAds', header: 'Failed Drugs Ads', width: 200 },
    { name: 'FailedOffensiveLanguageAds', header: 'Failed Offensive Language Ads', width: 200 },
    { name: 'FailedViolenceAds', header: 'Failed Violence Ads', width: 200 },
    // Ad Refresh
    { name: 'RefreshImps', header: 'Refresh Ads', width: 200 },
    { name: 'RefreshThresholdEvents', header: 'IAS Refreshed Ads', width: 200 },
    { name: 'RefreshedAdRate', header: 'Refreshed Ad Rate', width: 200 },
    { name: 'IASRefreshedAdRate', header: 'IAS Refreshed Ad Rate', width: 200 },
    // Publicis Viewability
    {
      name: 'PublicisFullyInViewDisplayImps',
      header: 'Publicis Fully In View Display Impressions',
      width: 200,
    },
    {
      name: 'PublicisFullyInViewVideoImps',
      header: 'Publicis Fully In View Video Impressions',
      width: 200,
    },
    { name: 'PublicisDisplayViewableRate', header: 'Publicis Display Viewable Rate', width: 200 },
    { name: 'PublicisVideoViewableRate', header: 'Publicis Video Viewable Rate', width: 200 },
    // Blocked Reasons
    { name: 'BlockedContentImps', header: 'Blocked by Brand Safety', width: 200 },
    { name: 'BlockedSuitabilityImps', header: 'Blocked by Brand Suitability', width: 200 },
    { name: 'BlockedKeywordImps', header: 'Blocked by Custom Keyword Exclusion List', width: 200 },
    {
      name: 'BlockedCustomListImps',
      header: 'Blocked by Custom URL/App Exclusion List',
      width: 200,
    },
    { name: 'BlockedGeoImps', header: 'Blocked by Geo', width: 200 },
    { name: 'BlockedSuspiciousImps', header: 'Blocked by Invalid Traffic', width: 200 },
    { name: 'BlockedSeeThroughImps', header: 'Blocked by Invisible URL', width: 200 },
    { name: 'BlockedLanguageImps', header: 'Blocked by Target Language Mismatch', width: 200 },
    // Exposure
    { name: 'InViewAtPageOpenRate', header: 'In View At Page Open Rate', width: 200 },
    { name: 'InViewOver5sRate', header: 'In View > 5s Rate', width: 200 },
    { name: 'InViewOver15sRate', header: 'In View > 15s Rate', width: 200 },
    // Out Of View
    { name: 'MrcOvrNetImps', header: 'Not Enough Time', width: 200 },
    { name: 'MrcOvrNepImps', header: 'Not Enough Pixels', width: 200 },
    { name: 'MrcOvrNivImps', header: 'Never On Screen', width: 200 },
    { name: 'MrcOvrOofImps', header: 'Out Of Focus', width: 200 },
    { name: 'MrcOvrNsImps', header: 'Video Never Started', width: 200 },
    { name: 'MrcOvrUnkImps', header: 'Other Out Of View', width: 200 },
    // Failure Reasons
    { name: 'FailedContentImps', header: 'Failed for Brand Safety', width: 200 },
    { name: 'FailedSuitabilityImps', header: 'Failed for Brand Suitability', width: 200 },
    { name: 'FailedKeywordImps', header: 'Failed for Custom Keyword Exclusion List', width: 200 },
    {
      name: 'FailedCustomListImps',
      header: 'Failed for Custom URL/App Exclusion List',
      width: 200,
    },
    { name: 'FailedGeoImps', header: 'Failed for Geo', width: 200 },
    { name: 'FailedSuspiciousImps', header: 'Failed for Invalid Traffic', width: 200 },
    { name: 'FailedSeeThroughImps', header: 'Failed for Invisible URL', width: 200 },
    { name: 'FailedLanguageImps', header: 'Failed for Target Language Mismatch', width: 200 },
    // Blocked Risk Categories
    { name: 'BlockedContentAdultImps', header: 'Blocked Adult Ads', width: 200 },
    { name: 'BlockedContentAlcoholImps', header: 'Blocked Alcohol Ads', width: 200 },
    { name: 'BlockedContentHateImps', header: 'Blocked Hate Speech Ads', width: 200 },
    { name: 'BlockedContentDownloadsImps', header: 'Blocked Illegal Downloads Ads', width: 200 },
    { name: 'BlockedContentDrugsImps', header: 'Blocked Drugs Ads', width: 200 },
    { name: 'BlockedContentOffensiveImps', header: 'Blocked Offensive Language Ads', width: 200 },
    { name: 'BlockedContentViolenceImps', header: 'Blocked Violence Ads', width: 200 },
    // Video
    { name: 'VideoInViewRate', header: '% In View', width: 200 },
    { name: 'VideoOutOfViewRate', header: '% Out Of View', width: 200 },
    { name: 'VideoNeverStartedRate', header: '% Ad Never Started', width: 200 },
    { name: 'VideoCompletedRate', header: '% Completed', width: 200 },
    { name: 'VideoCompletedInViewRate', header: '% Completed In View', width: 200 },
    { name: 'VideoMutedRate', header: '% Muted', width: 200 },
    { name: 'VideoFullScreenRate', header: '% Full Screen', width: 200 },
    { name: 'VideoClickThroughRate', header: '% Click Through (CTR)', width: 200 },
    // Invalid Traffic by Type
    { name: 'FraudGivtImps', header: 'General Invalid Traffic (GIVT) Ads', width: 200 },
    { name: 'FraudNhtImps', header: 'Bots (SIVT) Ads', width: 200 },
    { name: 'FraudHaImps', header: 'Hidden Ads (SIVT)', width: 200 },
    { name: 'FraudDsImps', header: 'Domain Spoofing (SIVT)', width: 200 },
    { name: 'FraudIbImps', header: 'Incentivized Browsing (RVI) Ads', width: 200 },
    { name: 'FraudLspImps', header: 'Proxy Servers (RVI) Ads', width: 200 },
    {
      name: 'SophisticatedInvalidTrafficAds',
      header: 'Sophisticated Invalid Traffic Ads',
      width: 200,
    },
    { name: 'ReducedValueInventoryAds', header: 'Reduced Value Inventory Ads', width: 200 },
    { name: 'GeneralInvalidTrafficRate', header: 'General Invalid Traffic Rate', width: 200 },
    {
      name: 'SophisticatedInvalidTrafficRate',
      header: 'Sophisticated Invalid Traffic Rate',
      width: 200,
    },
    { name: 'ReducedValueInventoryRate', header: 'Reduced Value Inventory Rate', width: 200 },
  ],
  27: [
    // IAS Youtube Report
    // Summary
    { name: 'TotalImps', header: 'Total Tracked Ads', width: 200 },
    { name: 'IVTAds', header: 'Invalid Traffic Ads', width: 200 },
    { name: 'MeasuredAds', header: 'Measured Ads', width: 200 },
    { name: 'ViewableImpression', header: 'Viewable Impressions', width: 200 },
    { name: 'IVTRate', header: 'Invalid Traffic Rate', width: 200 },
    { name: 'MeasuredRate', header: 'Measured Rate', width: 200 },
    { name: 'ViewabilityRate', header: 'Viewability Rate', width: 200 },
    // GroupM Viewability
    { name: 'GroupmFullyInViewDisplayImps', header: 'GroupM Display Impressions', width: 200 },
    { name: 'GroupmDisplayViewableRate', header: 'GroupM Display Viewable Rate', width: 200 },
    // Publicis Viewability
    {
      name: 'PublicisFullyInViewDisplayImps',
      header: 'Publicis Fully In View Display Impressions',
      width: 200,
    },
    { name: 'PublicisDisplayViewableRate', header: 'Publicis Display Viewable Rate', width: 200 },
  ],
  28: [
    // TTD Report
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'MediaCost', header: 'Media Cost', width: 200 },
    { name: 'BidResponses', header: 'Bid Count', width: 200 },
  ],
  29: [
    // Creative Review Report
    { name: 'ReviewStatus', header: 'Review Status', width: 200 },
    { name: 'NumberOfBlocks', header: 'Number of Blocks', width: 200 },
    { name: 'Username', header: 'Username', width: 200 },
  ],
  30: shareOfVoiceMetrics,
  31: [
    // Pod Hourly Report
    { name: 'CpmRev', header: 'Avg CPM Revenue', width: 200 },
    { name: 'CpmsRev', header: 'Avg CPM/s Revenue', width: 200 },
    { name: 'EventPodCompare', header: 'Auctions', width: 200 },
    { name: 'EventPodCompareWithAdvertiserRules', header: 'Auctions With Adv Rules', width: 200 },
    {
      name: 'EventPodCompareWithoutAdvertiserRules',
      header: 'Auctions Without Adv Rules',
      width: 200,
    },
    { name: 'NoBidSaverRev', header: 'Avg Revenue Without Publica Vault', width: 200 },
    { name: 'PodCompareRevenue', header: 'Avg Pod Compare Revenue', width: 200 },
    { name: 'PodDurationBackfilled', header: 'Avg Pod Duration Backfilled', width: 200 },
    {
      name: 'PodDurationOwnedByNonSOVBidder',
      header: 'Avg Pod Duration Owned By Non SOVBidder',
      width: 200,
    },
    {
      name: 'PodDurationOwnedBySOVBidder',
      header: 'Avg Pod Duration Owned By SOV Bidder',
      width: 200,
    },
  ],
  32: [
    // Advertiser Rule Report
    { name: 'PodCompareRevenue', header: 'Avg Pod Compare Revenue', width: 200 },
    { name: 'EventPodCompare', header: 'Auctions', width: 200 },
    { name: 'EventPodCompareWithAdvertiserRules', header: 'Auctions With Adv Rules', width: 200 },
    {
      name: 'EventPodCompareWithoutAdvertiserRules',
      header: 'Auctions Without Adv Rules',
      width: 200,
    },
    { name: 'CpmRev', header: 'Avg CPM Revenue', width: 200 },
    { name: 'CpmsRev', header: 'Avg CPM/s Revenue', width: 200 },
    { name: 'NoBidSaverRev', header: 'Avg Revenue Without Publica Vault', width: 200 },
    { name: 'AdvertiserRuleCpmRev', header: 'Avg Advertiser Rule Cpm Revenue', width: 200 },
    { name: 'AdvertiserRuleCpmsRev', header: 'Avg Advertiser Rule Cpm/s Revenue', width: 200 },
    { name: 'AdvertiserRulePodLift', header: 'Avg Advertiser Rule Revenue Pod Lift', width: 200 },
    { name: 'TTDResponses', header: 'TTD Rule Responses', width: 200 },
    { name: 'TTDSpend', header: 'TTD Rule Est. Spend', width: 200 },
    { name: 'TTDWins', header: 'TTD Rule Wins', width: 200 },
    { name: 'TTDLosses', header: 'TTD Rule Losses', width: 200 },
    { name: 'TTDLossesDoNotAir', header: 'TTD Rule Losses DNA', width: 200 },
    { name: 'TTDLossesCompSepSelf', header: 'TTD Rule Losses CS Self', width: 200 },
    { name: 'TTDLossesCompSepOther', header: 'TTD Rule Losses CS Other', width: 200 },
    { name: 'TTDLossesPrice', header: 'TTD Rule Losses Price', width: 200 },
    { name: 'TTDLossesTier', header: 'TTD Rule Losses Tier', width: 200 },
    { name: 'TTDLossesOther', header: 'TTD Rule Losses Other', width: 200 },
  ],
  33: apsBuyersMetric,
  34: [
    // Third Party Tracker Domains
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'BidResponses', header: 'Bid Responses', width: 200 },
    { name: 'EventStart', header: 'Start', width: 200 },
    { name: 'Quartile1', header: 'Quartile1', width: 200 },
    { name: 'Quartile2', header: 'Quartile2', width: 200 },
    { name: 'Quartile3', header: 'Quartile3', width: 200 },
    { name: 'Complete', header: 'Complete', width: 200 },
  ],
  35: [
    // Elea AI Billing Report
    { name: 'Impressions', header: 'Impressions', width: 200 },
  ],
  36: eleaAIAdQualityMetrics,
  // Bid Floor Settings Report Has No Real Metrics, but need this for order by
  37: [{ name: 'BidFloor', header: 'Bid Floor', width: 200 }],
  // Publica Vault Report Metrics
  38: [
    { name: 'BidSaverTrueLift', header: 'Publica Vault True Lift', width: 250 },
    { name: 'Impressions', header: 'Impressions', width: 200 },
  ],
  // auction fast
  41: [
    { name: 'DeliveredPodDuration', header: 'Delivered Pod Duration', width: 200 },
    { name: 'RenderedRequestedPodDuration', header: 'Rendered Requested Pod Duration', width: 250 },
    { name: 'RequestedPodDuration', header: 'Requested Pod Duration', width: 250 },
    { name: 'NativeCompanionClick', header: 'Native Companion Click', width: 200 },
    { name: 'NativeVideoClick', header: 'Native Video Click', width: 200 },
    { name: 'VPAIDErrors', header: 'VPAID Errors', width: 200 },
    { name: 'VPAIDStart', header: 'VPAID Started', width: 200 },
    { name: 'VPAIDImpressions', header: 'VPAID Impressions', width: 200 },
    { name: 'VPAIDQuartile1', header: 'VPAID 1st Quartiles', width: 200 },
    { name: 'VPAIDQuartile2', header: 'VPAID 2nd Quartiles', width: 200 },
    { name: 'VPAIDQuartile3', header: 'VPAID 3rd Quartiles', width: 200 },
    { name: 'VPAIDComplete', header: 'VPAID Completed', width: 200 },
    { name: 'FilledPodDuration', header: 'Filled Pod Duration', width: 250 },
    { name: 'BidAuctions', header: 'Auctions', width: 200 },
    { name: 'BidRequests', header: 'Bid Requests', width: 200 },
    { name: 'BidResponses', header: 'Bid Responses', width: 200 },
    { name: 'BidWons', header: 'Winning Bids', width: 200 },
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'WinRate', header: 'Win Rate', width: 200 },
    { name: 'BidResponseRate', header: 'Bid Response Rate', width: 200 },
    { name: 'CPMPerSecond', header: 'CPM Per Second', width: 200 },
    { name: 'PodClearRate', header: 'Pod Clear Rate', width: 200 },
    { name: 'PaidPodClearRate', header: 'Paid Pod Clear Rate', width: 200 },
    { name: 'PaidFilledPodDuration', header: 'Paid Filled Pod Duration', width: 200 },
    { name: 'TotalFilledPodDuration', header: 'Total Filled Pod Duration', width: 250 },
    { name: 'TotalPaidFilledPodDuration', header: 'Total Paid Filled Pod Duration', width: 200 },
    {
      name: 'EndpointRequests',
      header: getResellerTitle() !== 'Publica' ? 'Endpoint Requests' : 'Publica Endpoint Requests',
      width: 200,
    },
  ],
  // buyer fast
  42: [
    { name: 'VPAIDErrors', header: 'VPAID Errors', width: 200 },
    { name: 'VPAIDStart', header: 'VPAID Started', width: 200 },
    { name: 'VPAIDImpressions', header: 'VPAID Impressions', width: 200 },
    { name: 'VPAIDQuartile1', header: 'VPAID 1st Quartiles', width: 200 },
    { name: 'VPAIDQuartile2', header: 'VPAID 2nd Quartiles', width: 200 },
    { name: 'VPAIDQuartile3', header: 'VPAID 3rd Quartiles', width: 200 },
    { name: 'VPAIDComplete', header: 'VPAID Completed', width: 200 },
    { name: 'BidRequests', header: 'Bid Requests', width: 200 },
    { name: 'BidResponses', header: 'Bid Responses', width: 200 },
    { name: 'BidWons', header: 'Winning Bids', width: 200 },
    { name: 'Impressions', header: 'Impressions', width: 200 },
    { name: 'Revenue', header: 'Revenue', width: 200 },
    { name: 'RenderRate', header: 'Render Rate', width: 200 },
    { name: 'WinRate', header: 'Win Rate', width: 200 },
    { name: 'BidResponseRate', header: 'Bid Response Rate', width: 200 },
    { name: 'CPMPerSecond', header: 'CPM Per Second', width: 200 },
    { name: 'BidTimeouts', header: 'Bid Timeouts', width: 200 },
  ],
  45: [
    // SSAI User Activity Report Metrics
    { name: 'UniqueSessions', header: 'Unique Sessions', width: 200 },
    { name: 'UniqueViewers', header: 'Unique Viewers', width: 200 },
    { name: 'SessionsPerUser', header: 'Sessions Per User', width: 200 },
    { name: 'AvgWatchTimePerSession', header: 'Average Watch Time Per Session', width: 200 },
    { name: 'AvgWatchTimePerViewer', header: 'Average Watch Time Per Viewer', width: 200 },
  ],
};

const creativeTagEventColumns: IColumn[] = [
  { name: 'CreativeTagRequests', header: 'Creative Tag Requests', width: 200 },
  { name: 'CreativeTagResponses', header: 'Creative Tag Responses', width: 200 },
  { name: 'CreativeErrors', header: 'Creative Errors', width: 200 },
];

// add VMAP metrics if publisher = Philo
const vmapRequestResponseColumns: IColumn[] = [
  { name: 'VMAPRequests', header: 'VMAP Requests', width: 200 },
  { name: 'VMAPResponses', header: 'VMAP Responses', width: 200 },
  { name: 'RequestedVMAPDuration', header: 'Requested VMAP Duration', width: 200 },
  { name: 'FilledVMAPDuration', header: 'Filled VMAP Duration', width: 200 },
  { name: 'SharedRequestedVMAPDuration', header: 'Shared Requested VMAP Duration', width: 200 },
  { name: 'SharedFilledVMAPDuration', header: 'Shared Filled VMAP Duration', width: 200 },
];

// add VMAP Latency if Graph ID = 27 and publisher = Philo
const vmapLatencyColumn: IColumn = { name: 'VMAPLatency', header: 'VMAP Latency', width: 150 };

// add gross revenue and CPM columns if Graph ID = 39 and publisher = Premion or Publica
const grossRevenueColumn: IColumn = { name: 'GrossRevenue', header: 'Gross Revenue', width: 200 };
const grosseCPMColumn: IColumn = {
  name: 'AvgImpGrossECPM',
  header: 'Average Imp Gross eCPM',
  width: 200,
};

// Freewheel or Unity only
const ortbWinsColumn: IColumn = { name: 'ORTBWins', header: 'OpenRTB Wins', width: 200 };

// add APS Bids Submitted if publisher = Philo
const apsBidSubmittedColumn: IColumn = {
  name: 'APSBidsSubmitted',
  header: 'APS Bids Submitted',
  width: 200,
};

// Bidder Testing GroupBy Columns
const abTestingConfigNameGroupByColumn: IColumn = {
  name: 'AbTestingConfigName',
  header: 'Test Name',
  width: 200,
};
const abTestingTestNameGroupByColumn: IColumn = {
  name: 'AbTestingTestName',
  header: 'Side Name',
  width: 200,
};

// MultiCom and Canela only GroupBy column
const custom4GroupByColumn: IColumn = { name: 'Custom4', header: 'Custom 4', width: 200 };

// All Samsung Publisher GroupBy Columns
const operatingSystemGroupByColumn: IColumn = {
  name: 'OperatingSystem',
  header: 'Operating System',
  width: 250,
};
const apkBuildGroupByColumn: IColumn = { name: 'APKBuild', header: 'APK Build', width: 250 };
const samsungModelGroupByColumn: IColumn = {
  name: 'SamsungModel',
  header: 'Samsung Model',
  width: 250,
};
const subtierGroupByColumn: IColumn = {
  name: 'BidderSubTier',
  header: 'Bidder Subtier',
  width: 200,
};
const samsungGIVNParamExistsColumn: IColumn = {
  name: 'HasGooglePalSDKGivnParam',
  header: 'Has Google Pal SDK Givn Param',
  width: 200,
};
const buyerIDGroupByColumn: IColumn = {
  name: 'BuyerID',
  header: 'Buyer ID',
  width: 200,
};

// only for Samsung Publisher: add ActiveMargin and NetUnitCost Columns to Auction Report and Bidder Report
const samsungActiveMarginColumn: IColumn = {
  name: 'ActiveMargin',
  header: 'S-SSP CPM',
  width: 200,
};
const samsungNetUnitCostColumn: IColumn = {
  name: 'NetUnitCost',
  header: 'Unit Cost',
  width: 200,
};

// Samsung EU only GroupBy Column
const adomainGroupByColumn: IColumn = { name: 'Adomain', header: 'Adomain', width: 250 };

// Philo only GroupBy Columns
const shareTypeGroupByColumn: IColumn = { name: 'ShareType', header: 'Share Type', width: 250 };
const philoFallbackGroupByColumn: IColumn = {
  name: 'PhiloFallback',
  header: 'Fallback Type',
  width: 250,
};
const rokuAdKeysGroupByColumn: IColumn = { name: 'RokuAdKeys', header: 'Roku Ad Keys', width: 250 };

export function getMetricColumnsByReportID(
  reportType: string,
  publisherID: string,
  fwPublisherIDs: number[],
  groupByOptions: string[],
  enableAPSDemand: boolean,
  creativeTagEventColumnsEnabled?: boolean,
  metrics?: string[],
): IColumn[] {
  const graphID = getReportGraphID(reportType, groupByOptions || [], metrics || []);
  let cols: IColumn[] = [];

  // add ActiveMargin and NetUnitCostColumn only for Samsung publishers
  if (
    isSamsungPublisher(publisherID) &&
    (reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS ||
      reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS)
  ) {
    cols.push(samsungActiveMarginColumn, samsungNetUnitCostColumn);
  }

  if (isPhiloPublisher(publisherID) && reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS) {
    cols = cols.concat(vmapRequestResponseColumns);
    cols.push(vmapLatencyColumn);
  }

  if (
    enableAPSDemand &&
    (reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS ||
      reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS)
  ) {
    cols.push(apsBidSubmittedColumn);
  }

  cols = cols.concat(columnMetricsByReportType[reportType]);

  if (creativeTagEventColumnsEnabled && reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS) {
    cols = cols.concat(creativeTagEventColumns);
  }

  if (publisherID === '788' || publisherID === '1' || publisherID === '518') {
    if (graphID === '39') {
      // follow order of gross value columns in other graphs
      cols.splice(7, 0, grosseCPMColumn);
      cols.splice(9, 0, grossRevenueColumn);
    }
  }

  if (fwPublisherIDs.includes(parseInt(publisherID, 10)) || publisherID === '558') {
    if (
      reportType === REPORTTYPE_HEADERBIDDER_BYAUCTIONS ||
      reportType === REPORTTYPE_HEADERBIDDER_BYBUYERS
    ) {
      cols.push(ortbWinsColumn);
    }
  }

  return getColumns(cols, metrics);
}

export function generateBidderTierMenuList(report: IReport): IMenuList {
  const bidderTierMenuList: IMenuList = {};

  for (let idx = 1; idx < 11; idx++) {
    bidderTierMenuList[idx] = {
      ID: idx.toString(),
      selected: false,
      title: `Tier ${idx}`,
    };
  }

  if (!isEmpty(report)) {
    const splitBidderTiers = report.BidderTierFilter;
    for (const bidderTier of splitBidderTiers) {
      if (bidderTierMenuList[bidderTier]) {
        bidderTierMenuList[bidderTier].selected = true;
      }
    }
  }

  return bidderTierMenuList;
}
